// ── PROPOSALS TAB ─────────────────────────────────────────────────────────────
// Built fresh. DO NOT reference old ProposalsTab.jsx.
// All pricing via utils.js itemNet / itemClient / calcProposalDeposit.
// Eligibility via utils.js proposalEligible (blocks parentId items and
// any status >= Client Approved).

import React, { useState, useEffect, useRef } from 'react';
var h = React.createElement;
import { supabase } from '../lib/supabase';
import { useDetailJump } from '../lib/jump';
import { useAppContext } from './AppContext';
import { Lbl, SHead, IS } from './ui';
import {
  proposalEligible,
  itemNet,
  itemClient,
  assemblyNet,
  assemblyClient,
  clientDepositPct,
  itemDepositAmount,
  applyFreightMarkup,
  applyTaxMarkup,
  collectApprovalTargets,
  canRevertProposal,
  calcClientPrice,
  parsePrice,
  parseQty,
  generateProposalRef,
  generateInvoiceRef,
  projectLabelToCode,
  allSkusAssigned,
  statusColor,
  isCancelled,
  STATUSES,
  itemsBehindOnProposal,
  fmtMoney,
  hasAdminAccess,
  displayNameFor,
  buildDepositInvoicePayload as buildDepositInvoicePayloadPure,
  buildDepositInvoiceRecord,
  buildDepositInvoiceRecords,
  buildProposalPayloadPure,
  buildCustomsInvoicePayload,
  proposalStoragePath,
  aggregateProposalFees,
  ccFeeMul,
  eligibleForProject as utilsEligibleForProject,
  billingSummaryPeriodLabel,
} from '../utils';
import ConfirmationModal from './ConfirmationModal';

// ── ROLE GATE (module scope, exported for testing) ───────────────────────────
// Wraps `isOwner || hasAdminAccess(userEmail, firmSettings)` so the UI gate
// (button visibility), the function-layer gates inside advanceStatus /
// approveProposal / approveProposalFromModal, and the test suite all read from one
// source of truth.
//
// Used at four call sites: advanceStatus (Sent transition), approveProposal,
// approveProposalFromModal (the chained handler), and the toolbar render block
// that decides whether Mark Sent and Client Approved buttons are visible.
//
// Owner short-circuit lives here because `hasAdminAccess` deliberately
// excludes the owner — the owner is checked via the dedicated isOwner flag
// throughout the codebase and may not appear in studioTeam at all.
//
// Guard test: __tests__/proposalRoleGate.test.jsx.
export function canFinalizeProposal(args) {
  if (!args) return false;
  if (args.isOwner === true) return true;
  return hasAdminAccess(args.userEmail, args.firmSettings);
}

// ── CONSTANTS ─────────────────────────────────────────────────────────────────

var PROP_STATUSES = ['Draft', 'Ready for Review', 'Sent', 'Approved'];

function extractProposalSeq(ref) {
  var m = (ref || '').match(/-P-(\d+)$/i);
  return m ? parseInt(m[1], 10) : 0;
}

var STATUS_STYLE = {
  'Draft':            { bg: '#F7F7F5', color: '#888880', border: '#D8D7D2' },
  'Ready for Review': { bg: '#FFF8E1', color: '#BF6800', border: '#F5C800' },
  'Sent':             { bg: '#E8F5E9', color: '#1A5C38', border: '#A5D6A7' },
  'Approved':         { bg: '#EEF2FF', color: '#3730A3', border: '#A5B4FC' },
};

// ── FORMATTING ────────────────────────────────────────────────────────────────

function fmtDate(iso) {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  } catch(e) { return iso.slice(0, 10); }
}

// ── BLANK PROPOSAL ────────────────────────────────────────────────────────────

function blankProposal() {
  return {
    id: crypto.randomUUID ? crypto.randomUUID() : (Date.now() + '-' + Math.random()),
    proposal_ref: '',
    proposal_name: '',
    project: '',
    clientId: '',
    status: 'Draft',
    items: [],
    version: 1,
    total_net: 0,
    total_client: 0,
    total_deposit: 0,
    depositPct: 80,
    notes: '',
    created_at: new Date().toISOString(),
    sent_at: null,
    approved_at: null,
  };
}

// ── SHARED CHROME ─────────────────────────────────────────────────────────────

var S = {
  wrap: {
    fontFamily: "'DM Sans', sans-serif",
    fontSize: 12,
    color: '#111111',
    background: '#FFFFFF',
  },
  toolbar: {
    display: 'flex',
    alignItems: 'center',
    height: 44,
    padding: '0 14px',
    gap: 8,
    borderBottom: '0.5px solid #D8D7D2',
    background: '#F7F7F5',
  },
  backBtn: {
    background: 'none',
    border: 'none',
    fontSize: 11,
    color: '#888880',
    cursor: 'pointer',
    fontFamily: 'inherit',
    padding: 0,
    flexShrink: 0,
  },
  slash: {
    color: '#D8D7D2',
    fontSize: 11,
    flexShrink: 0,
  },
  pageTitle: {
    fontSize: 13,
    fontWeight: 500,
    color: '#111111',
    flex: 1,
    minWidth: 0,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  // Toolbar action buttons
  tbtnGhost: {
    height: 28, padding: '0 10px', borderRadius: 5,
    border: '0.5px solid #D8D7D2', background: '#FFFFFF',
    cursor: 'pointer', fontSize: 11, fontWeight: 500,
    color: '#111111', fontFamily: 'inherit', whiteSpace: 'nowrap',
  },
  tbtnPrimary: {
    height: 28, padding: '0 11px', borderRadius: 5,
    border: '0.5px solid #111111', background: '#111111',
    cursor: 'pointer', fontSize: 11, fontWeight: 500,
    color: '#FFFFFF', fontFamily: 'inherit', whiteSpace: 'nowrap',
  },
  tbtnDanger: {
    height: 28, padding: '0 10px', borderRadius: 5,
    border: '0.5px solid #C8322A', background: '#FFFFFF',
    cursor: 'pointer', fontSize: 11, fontWeight: 500,
    color: '#C8322A', fontFamily: 'inherit', whiteSpace: 'nowrap',
  },
  // Context strip below toolbar
  ctxStrip: {
    display: 'flex',
    alignItems: 'center',
    minHeight: 34,
    padding: '0 14px',
    borderBottom: '0.5px solid #D8D7D2',
    background: '#FFFFFF',
    flexWrap: 'wrap',
    gap: 0,
    fontSize: 11,
    color: '#555552',
  },
  ctxPill: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 4,
    padding: '6px 12px 6px 0',
    borderRight: '0.5px solid #EFEFEC',
    marginRight: 12,
  },
  ctxLabel: {
    fontSize: 10,
    color: '#888880',
  },
  content: {
    padding: '20px 14px',
    background: '#FFFFFF',
  },
};

// ── STATUS BADGE ──────────────────────────────────────────────────────────────

function StatusPill(p) {
  var s = STATUS_STYLE[p.status] || STATUS_STYLE['Draft'];
  return (
    <span style={{
      background: s.bg, color: s.color,
      border: '0.5px solid ' + s.border,
      padding: '2px 8px', borderRadius: 3,
      fontSize: 10, fontWeight: 500,
      letterSpacing: '0.04em',
      flexShrink: 0,
    }}>{p.status}</span>
  );
}

// ── TABLE HEADER CELL ─────────────────────────────────────────────────────────

function TH(p) {
  return (
    <th style={{
      padding: '6px 10px',
      textAlign: p.right ? 'right' : 'left',
      fontSize: 10, fontWeight: 500,
      letterSpacing: '0.07em', textTransform: 'uppercase',
      color: '#888880', whiteSpace: 'nowrap',
      borderBottom: '0.5px solid #D8D7D2',
      background: '#F7F7F5',
    }}>{p.children}</th>
  );
}

// ── MAIN COMPONENT ────────────────────────────────────────────────────────────

export default function ProposalsTab2() {
  var ctx = useAppContext();
  var proposals = ctx.proposals || [];
  var setProposals = ctx.setProposals;
  var allItems = ctx.items || [];
  var allAssemblies = ctx.assemblies || [];
  var clients = ctx.clients || [];
  var projects = ctx.projects || [];
  var updateItem = ctx.updateItem;
  var saveProposalToDb = ctx.saveProposalToDb;
  var patchProposalInDb = ctx.patchProposalInDb;
  var deleteProposalFromDb = ctx.deleteProposalFromDb;
  var autoAssignItemNumber = ctx.autoAssignItemNumber;
  var computeItemNumber = ctx.computeItemNumber;
  var dispenseDocSequence = ctx.dispenseDocSequence;
  var allInvoices = ctx.invoices || [];
  var saveInvoice = ctx.saveInvoice;
  var firmSettings = ctx.firmSettings || {};
  var isOwner = ctx.isOwner;
  // Mark Sent + Client Approved (owner+admin). The admin predicate happens to be the
  // same predicate today, but they're semantically distinct — keeping them
  // separate so a future change to either gate doesn't accidentally widen
  // or narrow the other.
  var canFinalize = canFinalizeProposal({
    isOwner: isOwner,
    userEmail: ctx.userEmail,
    firmSettings: ctx.firmSettings,
  });

  var vs = useState('list'); var view = vs[0]; var setView = vs[1];
  var ds = useState(null); var detailId = ds[0]; var setDetailId = ds[1];

  // Honor detail jumps from other tabs.
  useDetailJump('proposals', function(id) { setDetailId(id); setView('detail'); });
  var ns = useState(blankProposal()); var newProp = ns[0]; var setNewProp = ns[1];
  var ss = useState([]); var selectedIds = ss[0]; var setSelectedIds = ss[1];
  var pdfs = useState(false); var pdfLoading = pdfs[0]; var setPdfLoading = pdfs[1];
  var pdfe = useState(''); var pdfErr = pdfe[0]; var setPdfErr = pdfe[1];
  var aps = useState(false); var approving = aps[0]; var setApproving = aps[1];
  var aes = useState(''); var approveErr = aes[0]; var setApproveErr = aes[1];
  // Three confirmation modals replacing the prior inline approveConfirm
  // two-step pattern. State shape:
  //   markSentModal:        null | { proposal, selectedPath: 'billing_summary' | 'off_cycle' }
  //   clientApprovedModal:  null | proposal-being-approved
  //   editOnSentModal:      null | { proposal, trigger: 'edit' | 'edit-items',
  //                                  selectedStatus: 'Ready for Review' | 'Sent' | 'Draft' }
  // ConfirmationModal in src/components/ConfirmationModal.jsx renders all three.
  var msm = useState(null); var markSentModal = msm[0]; var setMarkSentModal = msm[1];
  var cam = useState(null); var clientApprovedModal = cam[0]; var setClientApprovedModal = cam[1];
  var eosm = useState(null); var editOnSentModal = eosm[0]; var setEditOnSentModal = eosm[1];
  // revertToDraftModal — holds the proposal being reverted, null when closed.
  // Pure-status-flip companion to the existing inline revertConfirm pattern
  // for Approved→Sent. See revertProposalToDraft() handler.
  var rtdm = useState(null); var revertToDraftModal = rtdm[0]; var setRevertToDraftModal = rtdm[1];
  var rcs = useState(false); var revertConfirm = rcs[0]; var setRevertConfirm = rcs[1];
  var dcs = useState(false); var deleteConfirm = dcs[0]; var setDeleteConfirm = dcs[1];
  var pvs = useState(null); var pdfPreviewUrl = pvs[0]; var setPdfPreviewUrl = pvs[1];
  var pvf = useState(''); var pdfPreviewFilename = pvf[0]; var setPdfPreviewFilename = pvf[1];
  var svs = useState(false); var savingEdit = svs[0]; var setSavingEdit = svs[1];
  var ses = useState(''); var saveEditErr = ses[0]; var setSaveEditErr = ses[1];
  var pfs = useState(''); var projFilter = pfs[0]; var setProjFilter = pfs[1];
  var filterInitRef = useRef(false);

  // Auto-select first project on initial data load
  useEffect(function() {
    if (filterInitRef.current) return;
    var projs = Array.from(new Set(
      proposals.map(function(p) { return p.project || ''; }).filter(Boolean)
    )).sort();
    if (projs.length > 0) {
      filterInitRef.current = true;
      setProjFilter(projs[0]);
    }
  }, [proposals.length]); // eslint-disable-line react-hooks/exhaustive-deps

  // Re-sync stored totals whenever detail view opens -- fixes stale total_*
  // from before assembly-aware pricing or from item price changes after the
  // proposal was saved. Includes total_net so dashboards (BillingTab, etc.)
  // that read p.total_net stay in sync with current item state.
  useEffect(function() {
    if (view !== 'detail' || !detailId) return;
    var p = proposals.find(function(x) { return x.id === detailId; });
    if (!p) return;
    var liveItems = (p.items || []).map(function(id) { return allItems.find(function(it) { return it.id === id; }); }).filter(Boolean);
    var liveNet     = Math.round(liveItems.reduce(function(s, it) { return s + assemblyNet(it, allItems); }, 0) * 100) / 100;
    var liveClient  = Math.round(liveItems.reduce(function(s, it) { return s + assemblyClient(it, allItems); }, 0) * 100) / 100;
    var liveDeposit = Math.round(liveItems.reduce(function(s, it) { return s + itemDepositAmount(it, allItems); }, 0) * 100) / 100;
    if (liveNet !== p.total_net || liveClient !== p.total_client || liveDeposit !== p.total_deposit) {
      saveProposal(Object.assign({}, p, { total_net: liveNet, total_client: liveClient, total_deposit: liveDeposit }));
    }
  }, [view, detailId]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── HELPERS ────────────────────────────────────────────────────────────────

  function resolveProject(label) {
    return projects.find(function(pr) { return pr.label === label; }) || null;
  }

  function resolveClient(id) {
    return clients.find(function(cl) { return cl.id === id; }) || null;
  }

  function clientForProposal(p) {
    var proj = resolveProject(p.project);
    return resolveClient(p.clientId) || (proj ? resolveClient(proj.clientId) : null);
  }

  function proposalItems(p) {
    var ids = p.items || [];
    return allItems.filter(function(it) { return ids.indexOf(it.id) >= 0; });
  }

  function eligibleForProject(projectLabel) {
    // Delegates to the utils canonical (2026-07-29) — this local copy had
    // drifted into a duplicate and silently missed the OF/BO admission
    // rule (classification items join proposals at any pre-approval
    // status; no Presented-to-Client walk required).
    return utilsEligibleForProject(projectLabel, allItems, newProp.items || [], newProp.id, proposals);
  }

  // Returns the saveProposalToDb promise so callers that need to await the
  // DB write before firing dependent work (e.g. the chained Client Approved
  // handler that pushes to QB after Gilda-side approval) can do so. Existing
  // fire-and-forget callers (most of the file) ignore the return value and
  // keep working unchanged.
  function saveProposal(p) {
    setProposals(function(prev) {
      var exists = prev.some(function(x) { return x.id === p.id; });
      if (exists) return prev.map(function(x) { return x.id === p.id ? p : x; });
      return prev.concat([p]);
    });
    return saveProposalToDb ? saveProposalToDb(p) : Promise.resolve(true);
  }

  async function deleteProposal(id) {
    var toDelete = proposals.find(function(x) { return x.id === id; });
    if (!toDelete) {
      window.alert('Proposal not found.');
      return;
    }
    // Guard: an Approved proposal has items at Client Approved with SKUs
    // assigned. Deleting silently reverts items past their lock point
    // and may break downstream invoice / PO references. Owner must
    // explicitly Revert to a draft state first via the Revert button.
    if (toDelete.status === 'Approved') {
      window.alert('Cannot delete an Approved proposal. Use the Revert button first to undo SKU assignments and unlock the items.');
      return;
    }
    // Guard: a proposal that's been bundled into a SENT billing summary
    // has been seen by the client. Hard block while the package is out;
    // once the summary is reopened as a draft (the sanctioned reversal),
    // deletion is allowed again (2026-08-03 — pre-fix the guard blocked
    // on the tag alone and named the summary by raw uuid).
    if (toDelete.billedOnSummaryId) {
      var bsr = await supabase.from('billing_summaries')
        .select('id, project_label, month, year, status, summary_type, sent_at')
        .eq('id', toDelete.billedOnSummaryId).maybeSingle();
      var bsRow = bsr && bsr.data;
      var bsName = bsRow
        ? (billingSummaryPeriodLabel(bsRow) + ' (' + (bsRow.project_label || '') + ')')
        : ('summary ' + toDelete.billedOnSummaryId);
      if (!bsRow || bsRow.status === 'sent' || bsRow.status === 'paid') {
        window.alert('Cannot delete proposal ' + (toDelete.proposal_ref || id) + ' — it was sent to the client in the ' + bsName +
          '. Reopen that summary as a draft first (Billing Summary \u2192 saved-record banner \u2192 Reopen as draft\u2026), then delete.');
        return;
      }
      if (!window.confirm('Proposal ' + (toDelete.proposal_ref || id) + ' rode the ' + bsName +
        ', which is now reopened as a draft. Deleting removes it from that package \u2014 the corrected re-send will not include it. Delete?')) {
        return;
      }
    }
    if (toDelete && updateItem) {
      // Await every item-revert save before treating the proposal as
      // deleted -- otherwise a save failure would leave items pointing
      // at a now-gone proposal id with no error surfaced to the user.
      var promises = (toDelete.items || []).map(function(itemId) {
        var it = allItems.find(function(x) { return x.id === itemId; });
        var revert = it && it.status === 'On Proposal'
          ? { proposalRef: '', proposalId: '', status: 'Presented to Client' }
          : { proposalRef: '', proposalId: '' };
        return updateItem(itemId, revert);
      });
      var results = await Promise.all(promises);
      var failed = results.filter(function(r) { return r === false; }).length;
      if (failed > 0) console.error('deleteProposal: ' + failed + ' item reverts failed');
    }
    setProposals(function(prev) { return prev.filter(function(p) { return p.id !== id; }); });
    if (deleteProposalFromDb) deleteProposalFromDb(id);
    setView('list');
    setDeleteConfirm(false);
  }

  // Sweep all items on a proposal — any that are still at a status earlier
  // than 'On Proposal' (e.g., 'Presented to Client' because the picker's
  // status-advance never fired) get advanced to 'On Proposal'. Cancelled
  // and post-Approval items are left alone. Used by both the Sent cascade
  // and the manual "Sync items" button on the out-of-sync banner.
  async function syncItemsToOnProposal(p) {
    if (!p || !updateItem) return 0;
    var behind = itemsBehindOnProposal(p.items || [], allItems);
    var promises = behind.map(function(it) {
      return updateItem(it.id, { status: 'On Proposal', proposalRef: p.proposal_ref || it.proposalRef || '', proposalId: p.id });
    });
    var results = await Promise.all(promises);
    var failed = results.filter(function(r) { return r === false; }).length;
    if (failed > 0) console.error('syncItemsToOnProposal: ' + failed + ' of ' + behind.length + ' updates failed');
    return behind.length;
  }

  // sentVia is the delivery path for the Ready→Sent transition; ignored on
  // Draft→Ready. Two values: 'billing_summary' (default — bundled into the
  // next monthly client billing summary) or 'off_cycle' (delivered out of
  // band; never auto-included on a summary). Drives BillingSummarySection's
  // auto-include filter post-Commit 4. Schema: 20260511_proposals_sent_via.sql.
  function advanceStatus(p, sentVia) {
    var idx = PROP_STATUSES.indexOf(p.status);
    // Stop at Sent — Approved goes through approveProposalFromModal (chained
    // approveProposal + pushProposalToQb) to also assign SKUs and push to QB.
    if (idx >= PROP_STATUSES.indexOf('Sent')) return;
    var next = PROP_STATUSES[idx + 1];
    // Function-layer role gate. Mark Ready for Review (Draft → Ready) is open
    // to all staff per the role table; Mark Sent (Ready → Sent) requires
    // owner or admin per canFinalizeProposal. The UI gate at the toolbar
    // button level is the primary protection; this is belt-and-suspenders.
    if (next === 'Sent' && !canFinalize) {
      setApproveErr('Only the owner or admin team members can mark a proposal as Sent.');
      return;
    }
    var patch = { status: next };
    if (next === 'Sent') {
      var nowIso = new Date().toISOString();
      // Preserve the ORIGINAL send date on a re-send (2026-07-27). A
      // proposal walked back by accident (the Edit-on-Sent modal is
      // status-only) keeps its sent_at; re-marking Sent used to
      // overwrite it with today, silently rewriting when the client
      // actually received it. First-time sends still stamp now.
      patch.sent_at = p.sent_at || nowIso;
      // Two-path Mark Sent. Default to billing_summary so legacy callers
      // (none today, but defensive) don't silently skip the auto-include
      // filter and orphan the proposal off every summary.
      var path = sentVia === 'off_cycle' ? 'off_cycle' : 'billing_summary';
      patch.sentVia = path;
      patch.sentOffCycleAt = path === 'off_cycle' ? nowIso : null;
    }
    // Item-status cascade: any items still earlier than 'On Proposal' on this
    // proposal advance to 'On Proposal'. Predates this commit (see git
    // history) — kept here both because it's correct and because removing it
    // would silently break proposals built via bulk-add flows. Fire-and-forget;
    // the UI doesn't notify the bookkeeper that items were touched. Fires for
    // BOTH delivery paths (billing_summary and off_cycle) — the items are
    // promised to the client either way.
    if (next === 'Ready for Review' || next === 'Sent') syncItemsToOnProposal(p);
    saveProposal(Object.assign({}, p, patch));
    // Phase 5A.3 (2026-07-02) — off-cycle proposals are delivered out of
    // band the moment they're marked Sent, so this is their archive
    // moment: render the PDF, upload to the proposals bucket, freeze the
    // payload in document_sends. (Proposals sent via billing_summary are
    // archived by sendPackage when the package actually goes out.)
    // Fire-and-forget + best-effort: never blocks or reverts Mark Sent.
    if (next === 'Sent' && patch.sentVia === 'off_cycle') {
      captureOffCycleProposalSnapshot(p);
    }
  }

  // Renders + archives the as-sent PDF for an off-cycle proposal. Mirrors
  // the PO snapshot capture in PurchaseOrdersTab.downloadPoPdf and the
  // package-send proposal capture in BillingSummarySection.sendPackage.
  async function captureOffCycleProposalSnapshot(p) {
    if (!ctx || !ctx.recordDocumentSend) return;
    try {
      var payload = buildProposalPayload(p);
      var res = await fetch('/api/generate-proposal', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      if (!res.ok) throw new Error('PDF render failed (' + res.status + ')');
      var blob = await res.blob();
      var sentAt = new Date();
      var storagePath = proposalStoragePath(p.id, sentAt);
      var up = await supabase.storage
        .from('proposals')
        .upload(storagePath.replace(/^proposals\//, ''), blob, {
          contentType: 'application/pdf', upsert: false,
        });
      if (up.error) console.warn('off-cycle proposal PDF upload failed:', up.error.message);
      var client = clientForProposal(p) || {};
      await ctx.recordDocumentSend({
        documentType: 'proposal',
        documentId:   p.id,
        payload:      payload,
        storagePath:  up.error ? '' : storagePath,
        sentTo:       client.primaryEmail || client.email || '',
      });
    } catch (e) {
      console.warn('off-cycle proposal snapshot failed:', e && e.message);
    }
  }

  // Approve a Sent proposal: assign SKUs to items that need them, advance
  // every item to Client Approved status, and flip the proposal to Approved.
  //
  // INTERLEAVED compute+save loop -- each item's SKU is computed AND saved
  // before the next item's compute starts. This is required because component
  // letters (lighting/bulb pattern, parentId-based) are derived from a count
  // of existing children with item_number LIKE '<parent>%'. If we computed
  // all SKUs first and saved second, every sibling component would read
  // count=0 and try to assign letter A, hitting a unique-constraint conflict
  // on save. Saving each before the next compute lets the count read fresh.
  //
  // Trade-off: we lose the previous "fail fast before any saves" property.
  // Mitigation: the banner now derives from actual item state, so a partial
  // approval shows "Sync now" and the user can retry from where it stopped.
  // Returns Promise<{ ok: boolean, proposal?: object }>. ok===true means the
  // proposal flipped to Approved AND every item reached Client Approved with
  // a SKU AND the DB write of the new proposal state has committed. ok===false
  // means a SKU failed, an item update failed, or an exception was thrown —
  // in all cases setApproveErr has been set. The chained Client Approved
  // handler (approveProposalFromModal) uses the boolean to decide whether to fire
  // the QB push; it uses `proposal` (the freshly-Approved snapshot, including
  // any peer items collectApprovalTargets pulled in) so its caller has a
  // local ref synchronized with the DB without re-querying.
  async function approveProposal(p) {
    if (approving) return { ok: false };
    if (!canFinalize) {
      setApproveErr('Only the owner or admin team members can mark a proposal as Client Approved.');
      return { ok: false };
    }
    setApproving(true);
    setApproveErr('');
    try {
      var targets = collectApprovalTargets(p.items || [], allItems);
      var toApprove = targets.toApprove;

      // If collectApprovalTargets pulled in assemblyId peers that
      // weren't in p.items, sync them in -- otherwise they'd advance to
      // Client Approved + get SKUs but never appear on the deposit
      // invoice, final invoice, or proposal PDF (those all read p.items).
      var existingItems = (p.items || []).slice();
      var missing = toApprove
        .map(function(t) { return t.id; })
        .filter(function(id) { return existingItems.indexOf(id) < 0; });
      if (missing.length > 0) {
        p = Object.assign({}, p, { items: existingItems.concat(missing) });
      }

      var skuFailures = [];
      // Track the patches we apply so the deposit-invoice builder downstream
      // can see post-approval state (status='Client Approved' + itemNumber)
      // even if updateItem's React-state propagation lags by a tick.
      var appliedPatches = {};
      if (updateItem && computeItemNumber) {
        for (var i = 0; i < toApprove.length; i++) {
          var t = toApprove[i];
          var patch = {};
          // Forward-only: advance to Client Approved, never walk back.
          // Matters for "Sync now" recovery (2026-07-08 SKU-loss repair):
          // a proposal's items may have legitimately advanced (Deposit
          // Paid, PO Issued, ...) before a missing SKU was noticed —
          // re-running approval must backfill the SKU without regressing
          // their statuses.
          if (STATUSES.indexOf(t.status) < STATUSES.indexOf('Client Approved')) {
            patch = { status: 'Client Approved' };
          }
          if (!t.itemNumber) {
            var num = await computeItemNumber(t);
            if (!num) { skuFailures.push(t); continue; }
            patch.itemNumber = num;
          }
          if (Object.keys(patch).length === 0) continue;  // already synced
          await updateItem(t.id, patch);
          appliedPatches[t.id] = patch;
        }
      }

      if (skuFailures.length > 0) {
        var names = skuFailures.map(function(x) { return x.item || x.productName || x.id; }).join(', ');
        setApproveErr('SKU assignment failed for: ' + names + '. Set project + room number on those items, then click "Sync now" to retry.');
        setApproving(false);
        return { ok: false };
      }

      var approvedProposal = Object.assign({}, p, {
        status: 'Approved',
        approved_at: new Date().toISOString(),
      });
      // Await the DB write so callers (the chained Client Approved handler)
      // can fire the QB push knowing the freshly-Approved row — including any
      // peer items added above — has committed to Postgres. Without this
      // await the QB push race-loaded the prior items array.
      await saveProposal(approvedProposal);

      // Workflow document: deposit invoice row with full line-item snapshot.
      //
      // This is the ONLY path that creates a deposit invoice. Approval
      // writes the FULL record (lineItems + per-fee breakdown + totals)
      // via buildDepositInvoiceRecord; the resulting snapshot is the
      // canonical commitment record between firm and client and is
      // treated as immutable once written. The Invoices tab and Ledger
      // view re-render the PDF by reading inv.lineItems and posting it
      // to /api/generate-deposit-invoice — they never rebuild the
      // record from current proposal state.
      //
      // The toolbar "↓ Deposit Invoice" button + previewDepositInvoice
      // function were removed 2026-05-25 because they silently
      // overwrote this snapshot with current proposal data on every
      // click — a regression vector for post-CO drift. Post-CO
      // revisions are a future "Revise Deposit Invoice" spec (explicit
      // new revision row, original preserved).
      //
      // Fire-and-forget by design — if the builder throws (data
      // integrity issue: item not at Client Approved, missing SKU,
      // etc.) or saveInvoice fails (transport / schema drift),
      // approval has already committed and the proposal stays
      // Approved. The bookkeeper can re-trigger approval recovery via
      // the existing "Sync now" path (the original creation flow).
      //
      // `effectiveItems` overlays the just-applied SKU + status patches
      // onto allItems — updateItem's React-state propagation may lag by a
      // tick, and the pure builder's "items must be at Client Approved
      // with SKU" guard would otherwise fire against stale state.
      try {
        if (saveInvoice) {
          // Per-item deposit invoices (since 2026-05-26 sub-commit 2): one
          // invoice record PER item on the proposal, with letter-suffixed
          // refs (INV-001-A-DEP / -B-DEP / ...). Legacy detection: an
          // existing PROPOSAL-LEVEL deposit invoice (itemId empty / no
          // itemId field — the pre-2026-05-26 shape) covers all items;
          // re-approval skips creation so the legacy row is preserved.
          // For new approvals on already-per-item proposals, the existing
          // per-item set is detected and approval is also a no-op.
          var existingForProposal = (allInvoices || []).filter(function(inv) {
            return inv.proposalId === approvedProposal.id && inv.invoiceType === 'deposit';
          });
          var hasLegacy = existingForProposal.some(function(inv) {
            return !inv.itemId;  // legacy shape: empty itemId
          });
          if (!hasLegacy) {
            var effectiveItems = (allItems || []).map(function(it) {
              return appliedPatches[it.id] ? Object.assign({}, it, appliedPatches[it.id]) : it;
            });

            // Pre-build a map of per-item existing records (covers the
            // partial-fail recovery case where some items got their invoice
            // last time and others didn't). Empty on a fresh approval.
            var existingByItem = {};
            existingForProposal.forEach(function(inv) {
              if (inv.itemId) existingByItem[inv.itemId] = inv;
            });

            var perItemBuilds = buildDepositInvoiceRecords({
              proposal:        approvedProposal,
              allItems:        effectiveItems,
              allInvoices:     allInvoices || [],
              projects:        projects,
              clients:         clients,
              firmSettings:    firmSettings,
              existingPerItem: existingByItem,
            });

            perItemBuilds.forEach(function(b) {
              saveInvoice(b.record);
              // Tag THIS item with THIS per-item invoice id. Each item
              // points at its own invoice now — itemUnpaidDepositShare
              // detects the per-item shape via inv.itemId === item.id and
              // takes the direct-math path (no pro-rata).
              if (updateItem) {
                var it = allItems.find(function(x) { return x.id === b.item.id; });
                if (it && it.clientDepositInvoiceId !== b.record.id) {
                  updateItem(b.item.id, { clientDepositInvoiceId: b.record.id });
                }
              }
            });
          }
        }
      } catch (depErr) {
        // Approval already committed; the row creation is a follow-up that
        // can be recovered manually via the "↓ Deposit Invoice" button.
        console.warn('approveProposal: deposit invoice row creation failed (approval not rolled back):', depErr);
      }

      setApproving(false);
      return { ok: true, proposal: approvedProposal };
    } catch (err) {
      console.error('approveProposal error:', err);
      setApproveErr('Approval failed: ' + (err && err.message ? err.message : 'unknown error'));
      setApproving(false);
      return { ok: false };
    }
  }

  // Client Approved handler — approval only.
  //
  // History: pre-SPEC-1 this function chained `pushProposalToQb` after
  // `approveProposal` succeeded, so every approval auto-fired a QB
  // Estimate push. SPEC 1 (2026-05-21) gated the chain behind
  // `firmSettings.qbSyncMode='auto'`. The 2026-05-24 cleanup removed
  // the auto mode entirely — every QB push is now a manual click from
  // the QuickBooks tab. This function exists to keep the UI button
  // wired to a single entry point (the Client Approved confirmation
  // modal) and to apply the owner-or-admin role gate before
  // `approveProposal` does any DB work.
  //
  // Guard tests: __tests__/proposalRoleGate.test.jsx +
  // __tests__/proposalApproveNoCascade.test.jsx.
  async function approveProposalFromModal(p) {
    if (!canFinalize) {
      setApproveErr('Only the owner or admin team members can mark a proposal as Client Approved.');
      return { ok: false };
    }
    return await approveProposal(p);
  }

  // Walks an Approved proposal back to Sent so items can be edited (e.g.
  // bundling additional scope before deposit invoicing). Owner-only. Blocked
  // when any item has advanced past Client Approved — those need a Change Order.
  async function revertProposal(p) {
    if (approving) return;
    var check = canRevertProposal(p.items || [], allItems);
    if (!check.canRevert) {
      var names = check.blockers.map(function(b) { return (b.item || b.id) + ' (' + b.status + ')'; }).join(', ');
      setApproveErr('Cannot revert — these items are past Client Approved: ' + names + '. Use a Change Order instead.');
      setRevertConfirm(false);
      return;
    }
    setApproving(true);
    setApproveErr('');
    var targets = collectApprovalTargets(p.items || [], allItems);
    if (updateItem) {
      // Await every revert save before flipping the proposal back to Sent.
      var promises = targets.toApprove
        .filter(function(it) { return it.status === 'Client Approved'; })
        .map(function(it) { return updateItem(it.id, { status: 'Presented to Client' }); });
      var results = await Promise.all(promises);
      var failed = results.filter(function(r) { return r === false; }).length;
      if (failed > 0) {
        setApproveErr('Revert partially failed (' + failed + ' items did not save). Reload and check item states before retrying.');
        setApproving(false);
        setRevertConfirm(false);
        return;
      }
    }
    saveProposal(Object.assign({}, p, {
      status: 'Sent',
      approved_at: null,
    }));
    setApproving(false);
    setRevertConfirm(false);
  }

  // Walks a Ready-for-Review proposal back to Draft so the reviewer (Cimon
  // or Dan) can edit it before approving. Owner + admin only via
  // canFinalizeProposal.
  //
  // Intentionally does NOT cascade to items. Items on a Draft proposal are
  // already at 'On Proposal' — the forward cascade fires on item-add via
  // createProposal (ProposalsTab2.jsx:1335-1341), NOT on Mark Ready / Mark
  // Sent. So flipping the proposal back to Draft leaves items in their
  // correct steady state. This differs from the Approved → Sent revert
  // (which DOES walk items back) because that revert unwinds a client
  // decision and SKU assignment; this one is a low-consequence editorial
  // step on a proposal that's not yet been delivered.
  //
  // No timestamp clearing — there's no ready_for_review_at column to clear.
  function revertProposalToDraft(p) {
    if (!p || p.status !== 'Ready for Review') return Promise.resolve(false);
    return saveProposal(Object.assign({}, p, { status: 'Draft' }));
  }

  function calcTotals(itemIds) {
    var its = allItems.filter(function(it) { return itemIds.indexOf(it.id) >= 0; });
    var net    = its.reduce(function(s, it) { return s + assemblyNet(it, allItems); }, 0);
    var client = its.reduce(function(s, it) { return s + assemblyClient(it, allItems); }, 0);
    var deposit = its.reduce(function(s, it) { return s + itemDepositAmount(it, allItems); }, 0);
    return {
      total_net:    Math.round(net * 100) / 100,
      total_client: Math.round(client * 100) / 100,
      total_deposit: Math.round(deposit * 100) / 100,
    };
  }

  // ── PDF HELPERS ────────────────────────────────────────────────────────────

  // For assemblies, the parent netPrice is 0 and pricing lives in components.
  // Synthesize a single netPrice/markupPct for the PDF so client_total() works correctly.
  // Also attach the assemblyName (looked up from the assemblies table via
  // assemblyId) so the PDF can show "Sofa — Workroom" context on each item.
  function resolveItemsForPdf(ids) {
    return allItems
      .filter(function(it) { return ids.indexOf(it.id) >= 0; })
      // Cancelled items never print (2026-09-03, the 26HOB-P-014 mattress):
      // a re-downloaded proposal/customs PDF was still showing cancelled
      // items at full price. The as-sent archive in document_sends keeps
      // the original; live regeneration reflects the current proposal.
      .filter(function(it) { return it.status !== 'Cancelled'; })
      .map(function(it) {
        var asm = it.assemblyId ? (allAssemblies.find(function(a) { return a.id === it.assemblyId; }) || null) : null;
        var assemblyName = asm && asm.name ? asm.name : '';
        var comps = allItems.filter(function(c) { return c.parentId === it.id; });
        if (comps.length === 0) return Object.assign({}, it, { assemblyName: assemblyName });
        var qty = parseQty(it.qty) || 1;
        var netTotal = assemblyNet(it, allItems);
        var clientTotal = assemblyClient(it, allItems);
        var netPerUnit = netTotal / qty;
        var synthMarkup = netPerUnit > 0 ? ((clientTotal / netTotal) - 1) * 100 : 30;
        return Object.assign({}, it, {
          netPrice: Math.round(netPerUnit * 100) / 100,
          markupPct: Math.round(synthMarkup * 100) / 100,
          assemblyName: assemblyName,
        });
      });
  }

  // ── PDF DOWNLOAD ───────────────────────────────────────────────────────────

  // Builds the payload for /api/generate-proposal. Pure helper in utils.js
  // computes assembly-aware unit / total / depositAmt per item AND the five
  // per-fee totals + grandTotal + depositTotal at the top level — the Python
  // generator no longer does pricing math, just renders what it's handed.
  function buildProposalPayload(p) {
    var pItems = resolveItemsForPdf(p.items || []);
    return buildProposalPayloadPure({
      proposal: p,
      items:    pItems,
      client:   clientForProposal(p) || {},
      project:  resolveProject(p.project) || { label: p.project },
      allItems: allItems,
    });
  }

  function downloadPdf(p) {
    setPdfLoading(true); setPdfErr('');
    fetch('/api/generate-proposal', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(buildProposalPayload(p)),
    })
    .then(function(res) {
      if (!res.ok) return res.json().then(function(d) { throw new Error(d.error || 'PDF failed'); });
      return res.blob();
    })
    .then(function(blob) {
      var url = URL.createObjectURL(blob);
      var a = document.createElement('a');
      a.href = url;
      a.download = 'Prop-' + (p.proposal_ref || 'draft') + '_' + (p.project || '').replace(/[^a-zA-Z0-9]/g, '') + '.pdf';
      document.body.appendChild(a); a.click(); document.body.removeChild(a);
      URL.revokeObjectURL(url);
      setPdfLoading(false);
    })
    .catch(function(e) { setPdfErr(e.message || 'PDF generation failed'); setPdfLoading(false); });
  }

  // Customs invoice (2026-07-19) — a one-page "INVOICE — PAID" print for
  // customs/export paperwork. PRESENTATION ONLY: no invoices row, no ref
  // minted, no payment records touched, nothing for QB or reconciliation.
  // The PAID line is the operator's deliberate customs-presentation
  // choice — clicking the button IS the decision. Numbers come from
  // buildCustomsInvoicePayload (wrapping buildProposalPayloadPure), so
  // they're byte-identical to the proposal PDF.
  function downloadCustomsInvoice(p) {
    setPdfLoading(true); setPdfErr('');
    var pItems = resolveItemsForPdf(p.items || []);
    fetch('/api/generate-customs-invoice', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(buildCustomsInvoicePayload({
        proposal: p,
        items:    pItems,
        client:   clientForProposal(p) || {},
        project:  resolveProject(p.project) || { label: p.project },
        allItems: allItems,
      })),
    })
    .then(function(res) {
      if (!res.ok) return res.json().then(function(d) { throw new Error(d.error || 'PDF failed'); });
      return res.blob();
    })
    .then(function(blob) {
      var url = URL.createObjectURL(blob);
      var a = document.createElement('a');
      a.href = url;
      a.download = 'Customs-' + (p.proposal_ref || 'draft') + '_' + (p.project || '').replace(/[^a-zA-Z0-9]/g, '') + '.pdf';
      document.body.appendChild(a); a.click(); document.body.removeChild(a);
      URL.revokeObjectURL(url);
      setPdfLoading(false);
    })
    .catch(function(e) { setPdfErr(e.message || 'PDF generation failed'); setPdfLoading(false); });
  }

  function previewPdf(p) {
    setPdfLoading(true); setPdfErr('');
    var fname = (p.proposal_ref || 'draft').replace(/[^a-zA-Z0-9-]/g, '') + '.pdf';
    fetch('/api/generate-proposal', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(buildProposalPayload(p)),
    })
    .then(function(res) {
      if (!res.ok) return res.json().then(function(d) { throw new Error(d.error || 'PDF failed'); });
      return res.blob();
    })
    .then(function(blob) {
      if (pdfPreviewUrl) URL.revokeObjectURL(pdfPreviewUrl);
      setPdfPreviewUrl(URL.createObjectURL(blob));
      setPdfPreviewFilename(fname);
      setPdfLoading(false);
    })
    .catch(function(e) { setPdfErr(e.message || 'PDF generation failed'); setPdfLoading(false); });
  }

  // ── DEPOSIT INVOICE GENERATION ────────────────────────────────────────────
  // Pure builder lives in utils.js (buildDepositInvoicePayload) so it can be
  // unit-tested without mounting the React tree. This thin wrapper forwards
  // the component's contextual state (allItems / projects / clients /
  // firmSettings / allInvoices) into the pure function. Behavior is
  // identical to the prior inline version — extraction is for testability
  // only. See depositInvoiceFees.test.js for the per-fee breakdown contract.
  function buildDepositInvoicePayload(p) {
    return buildDepositInvoicePayloadPure({
      proposal:     p,
      allItems:     allItems,
      allInvoices:  allInvoices,
      projects:     projects,
      clients:      clients,
      firmSettings: firmSettings,
    });
  }

  // previewDepositInvoice removed 2026-05-25 alongside its toolbar
  // button. The function rebuilt line items / totals / fee breakdown
  // from current proposal state and upserted the existing record on
  // every call — silently mutating the canonical deposit-invoice
  // snapshot when CO drift had shifted item values. Per the deposit-
  // invoice immutability invariant, regenerating a PDF must read the
  // stored snapshot, never rewrite it. The remaining call site that
  // uses buildDepositInvoiceRecord (approveProposal below) only
  // creates the initial record — it does not pass `existing`, so the
  // mutation path is gone. InvoicesTab's modal regeneration already
  // reads from inv.lineItems (stored snapshot) and posts that to the
  // PDF endpoint without touching the record.

  // ── PDF PREVIEW MODAL ─────────────────────────────────────────────────────

  if (pdfPreviewUrl) {
    return (
      <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)', zIndex: 1000, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
        <div style={{ background: '#fff', borderRadius: 6, overflow: 'hidden', width: '84vw', height: '92vh', display: 'flex', flexDirection: 'column', boxShadow: '0 8px 40px rgba(0,0,0,0.3)' }}>
          <div style={{ display: 'flex', alignItems: 'center', padding: '8px 14px', borderBottom: '0.5px solid #D8D7D2', background: '#F7F7F5', gap: 8 }}>
            <span style={{ flex: 1, fontFamily: "'DM Sans', sans-serif", fontSize: 12, fontWeight: 500 }}>Proposal Preview</span>
            <button style={S.tbtnGhost} onClick={function() {
              var a = document.createElement('a'); a.href = pdfPreviewUrl;
              a.download = pdfPreviewFilename;
              document.body.appendChild(a); a.click(); document.body.removeChild(a);
            }}>↓ Download</button>
            <button style={S.tbtnGhost} onClick={function() {
              URL.revokeObjectURL(pdfPreviewUrl); setPdfPreviewUrl(null);
            }}>Close</button>
          </div>
          <iframe src={pdfPreviewUrl} style={{ flex: 1, border: 'none', width: '100%' }} title="Proposal PDF" />
        </div>
      </div>
    );
  }

  // ── STEP 1: METADATA FORM ─────────────────────────────────────────────────

  if (view === 'new1') {
    var step1Valid = newProp.proposal_ref.trim() && newProp.project;
    var selProj = resolveProject(newProp.project);
    var autoClient = selProj ? resolveClient(selProj.clientId) : null;

    return (
      <div style={S.wrap}>
        <div style={S.toolbar}>
          <button style={S.backBtn}
            onClick={function() { setView('list'); setNewProp(blankProposal()); }}>
            ← Proposals
          </button>
          <span style={S.slash}>/</span>
          <span style={S.pageTitle}>New Proposal</span>
          <button style={Object.assign({}, S.tbtnGhost,
            { opacity: step1Valid ? 1 : 0.4, cursor: step1Valid ? 'pointer' : 'default' })}
            disabled={!step1Valid}
            onClick={function() {
              if (!step1Valid) return;
              var draft = Object.assign({}, newProp, { status: 'Draft', items: [] });
              saveProposal(draft);
              setDetailId(draft.id);
              setNewProp(draft);
              setSelectedIds([]);
              setView('new2');
            }}>
            Save as Draft
          </button>
          <button style={Object.assign({}, S.tbtnPrimary,
            { opacity: step1Valid ? 1 : 0.4, cursor: step1Valid ? 'pointer' : 'default' })}
            disabled={!step1Valid}
            onClick={function() { if (step1Valid) { setSelectedIds([]); setView('new2'); } }}>
            Select Items →
          </button>
        </div>

        <div style={Object.assign({}, S.content, { maxWidth: 620 })}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 12 }}>
            <div>
              <Lbl required>Project</Lbl>
              <select style={Object.assign({}, IS, { background: '#fff' })}
                value={newProp.project}
                onChange={function(e) {
                  var projLabel = e.target.value;
                  var projObj = projects.find(function(pr) { return pr.label === projLabel; });
                  var codeToUse = (projObj && projObj.projectCode) ? projObj.projectCode : projectLabelToCode(projLabel);
                  var autoRef = projLabel ? generateProposalRef(codeToUse, proposals) : '';
                  setNewProp(Object.assign({}, newProp, {
                    project: projLabel,
                    clientId: projObj ? (projObj.clientId || '') : '',
                    proposal_ref: autoRef,
                  }));
                }}>
                <option value="">— Select project —</option>
                {projects.map(function(pr) {
                  return <option key={pr.id} value={pr.label}>{pr.label}</option>;
                })}
              </select>
            </div>
            <div>
              <Lbl required>Proposal Number</Lbl>
              <input style={IS} placeholder="Select project to auto-generate"
                value={newProp.proposal_ref}
                onChange={function(e) { setNewProp(Object.assign({}, newProp, { proposal_ref: e.target.value })); }} />
            </div>
          </div>

          <div style={{ marginBottom: 12 }}>
            <Lbl>Proposal Name</Lbl>
            <input style={IS} placeholder="e.g. Primary Bedroom — Round 1"
              value={newProp.proposal_name}
              onChange={function(e) { setNewProp(Object.assign({}, newProp, { proposal_name: e.target.value })); }} />
          </div>

          <div style={{ marginBottom: 12 }}>
            <Lbl>Client</Lbl>
            {!newProp.project
              ? <div style={Object.assign({}, IS, { background: '#F7F7F5', color: '#888880', display: 'flex', alignItems: 'center', fontStyle: 'italic' })}>Select a project first</div>
              : !autoClient
                ? <div style={Object.assign({}, IS, { background: '#FFF8E1', color: '#BF6800', display: 'flex', alignItems: 'center' })}>No client assigned — set in Admin → Projects</div>
                : <div style={Object.assign({}, IS, { background: '#F7F7F5', color: '#111111', display: 'flex', alignItems: 'center', fontWeight: 500 })}>{autoClient.name}</div>
            }
          </div>

          <div>
            <Lbl>Notes</Lbl>
            <textarea style={Object.assign({}, IS, { resize: 'vertical', minHeight: 60 })}
              placeholder="Optional notes for this proposal..."
              value={newProp.notes}
              onChange={function(e) { setNewProp(Object.assign({}, newProp, { notes: e.target.value })); }}
              rows={3} />
          </div>
        </div>
      </div>
    );
  }

  // ── EDIT DETAILS (existing proposal metadata) ─────────────────────────────

  if (view === 'edit1') {
    var editValid = !!(newProp.proposal_ref && newProp.proposal_ref.trim() && newProp.project);
    var editProj = resolveProject(newProp.project);
    var editAutoClient = editProj ? resolveClient(editProj.clientId) : null;

    return (
      <div style={S.wrap}>
        <div style={S.toolbar}>
          <button style={S.backBtn} onClick={function() { setView('detail'); }}>
            ← {newProp.proposal_ref || 'Proposal'}
          </button>
          <span style={S.slash}>/</span>
          <span style={S.pageTitle}>Edit Details</span>
          <button style={S.tbtnGhost} onClick={function() { setView('detail'); }}>Cancel</button>
          {saveEditErr && (
            <span style={{ fontSize: 11, color: '#C8322A', whiteSpace: 'nowrap' }}>{saveEditErr}</span>
          )}
          <button
            style={Object.assign({}, S.tbtnPrimary, { opacity: (editValid && !savingEdit) ? 1 : 0.4, cursor: (editValid && !savingEdit) ? 'pointer' : 'default' })}
            disabled={!editValid || savingEdit}
            onClick={async function() {
              if (!editValid || savingEdit) return;
              setSavingEdit(true);
              setSaveEditErr('');
              // Edit Details touches metadata only. Patch those fields onto the
              // live row and the in-memory copy; never resend item_ids from
              // this tab's (possibly stale) snapshot.
              var patch = {
                proposal_ref: newProp.proposal_ref, proposal_name: newProp.proposal_name,
                project: newProp.project, clientId: newProp.clientId, notes: newProp.notes,
              };
              var updated = Object.assign({}, newProp);
              setProposals(function(prev) {
                var exists = prev.some(function(x) { return x.id === updated.id; });
                if (exists) return prev.map(function(x) { return x.id === updated.id ? Object.assign({}, x, patch) : x; });
                return prev.concat([updated]);
              });
              var ok = patchProposalInDb ? await patchProposalInDb(updated.id, patch)
                     : (saveProposalToDb ? await saveProposalToDb(updated) : true);
              setSavingEdit(false);
              if (ok) {
                setView('detail');
              } else {
                setSaveEditErr('Save failed — check connection and try again.');
              }
            }}>
            {savingEdit ? 'Saving…' : 'Save Changes'}
          </button>
        </div>
        <div style={Object.assign({}, S.content, { maxWidth: 620 })}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 12 }}>
            <div>
              <Lbl required>Proposal Number</Lbl>
              <input style={IS} value={newProp.proposal_ref}
                onChange={function(e) { setNewProp(Object.assign({}, newProp, { proposal_ref: e.target.value })); }} />
            </div>
            <div>
              <Lbl required>Project</Lbl>
              <select style={Object.assign({}, IS, { background: '#fff' })}
                value={newProp.project}
                onChange={function(e) {
                  var projLabel = e.target.value;
                  var projObj = projects.find(function(pr) { return pr.label === projLabel; });
                  setNewProp(Object.assign({}, newProp, {
                    project: projLabel,
                    clientId: projObj ? (projObj.clientId || '') : '',
                  }));
                }}>
                <option value="">— Select project —</option>
                {projects.map(function(pr) {
                  return <option key={pr.id} value={pr.label}>{pr.label}</option>;
                })}
              </select>
            </div>
          </div>
          <div style={{ marginBottom: 12 }}>
            <Lbl>Proposal Name</Lbl>
            <input style={IS} placeholder="e.g. Primary Bedroom — Round 1"
              value={newProp.proposal_name}
              onChange={function(e) { setNewProp(Object.assign({}, newProp, { proposal_name: e.target.value })); }} />
          </div>
          <div style={{ marginBottom: 12 }}>
            <Lbl>Client</Lbl>
            {!newProp.project
              ? <div style={Object.assign({}, IS, { background: '#F7F7F5', color: '#888880', display: 'flex', alignItems: 'center', fontStyle: 'italic' })}>Select a project first</div>
              : !editAutoClient
                ? <div style={Object.assign({}, IS, { background: '#FFF8E1', color: '#BF6800', display: 'flex', alignItems: 'center' })}>No client assigned — set in Admin → Projects</div>
                : <div style={Object.assign({}, IS, { background: '#F7F7F5', color: '#111111', display: 'flex', alignItems: 'center', fontWeight: 500 })}>{editAutoClient.name}</div>
            }
          </div>
          <div>
            <Lbl>Notes</Lbl>
            <textarea style={Object.assign({}, IS, { resize: 'vertical', minHeight: 60 })}
              placeholder="Optional notes for this proposal..."
              value={newProp.notes}
              onChange={function(e) { setNewProp(Object.assign({}, newProp, { notes: e.target.value })); }}
              rows={3} />
          </div>
        </div>
      </div>
    );
  }

  // ── STEP 2: ITEM PICKER ───────────────────────────────────────────────────

  if (view === 'new2') {
    var eligible = eligibleForProject(newProp.project);
    var roomGroups = {};
    eligible.forEach(function(it) {
      var key = it.roomNumber ? (it.roomNumber + (it.roomName ? ' — ' + it.roomName : '')) : 'No Room';
      if (!roomGroups[key]) roomGroups[key] = [];
      roomGroups[key].push(it);
    });
    var roomKeys = Object.keys(roomGroups).sort();
    var selTotals = calcTotals(selectedIds);

    function toggleItem(id) {
      setSelectedIds(function(prev) {
        return prev.indexOf(id) >= 0 ? prev.filter(function(x) { return x !== id; }) : prev.concat([id]);
      });
    }

    async function createProposal() {
      if (selectedIds.length === 0) return;
      var totals = calcTotals(selectedIds);
      var p = Object.assign({}, newProp, totals, { items: selectedIds.slice() });
      // Replace the locally-predicted proposal_ref with an authoritative
      // atomic dispense if we're creating a brand-new proposal (no
      // existing proposals.find match). Falls back to the predicted ref
      // if the RPC isn't available.
      if (dispenseDocSequence && p.project && !proposals.find(function(x){return x.id===p.id;})) {
        var projObj = projects.find(function(pr){return pr.label===p.project;});
        var projCode = projObj && projObj.projectCode;
        if (projCode) {
          var seq = await dispenseDocSequence('P', projCode);
          if (seq != null) p.proposal_ref = projCode.toUpperCase() + '-P-' + String(seq).padStart(3,'0');
        }
      }
      // Symmetry with collectApprovalTargets: when a peer of an assembly
      // is added or removed via the picker, walk peers so the assembly
      // moves as a unit. Without this, the picker silently orphans peers
      // (drop Workroom -> Fabric/Trim/Hardware stay on the proposal).
      var finalSelected = (function() {
        var setIds = new Set(selectedIds);
        // Pull in peers of every selected item.
        selectedIds.forEach(function(id) {
          var it = allItems.find(function(x) { return x.id === id; });
          if (it && it.assemblyId) {
            allItems.forEach(function(peer) {
              if (peer.assemblyId === it.assemblyId && peer.id !== id) setIds.add(peer.id);
            });
          }
        });
        return Array.from(setIds);
      })();
      var p2 = Object.assign({}, p, { items: finalSelected.slice() });
      saveProposal(p2);
      if (updateItem) {
        var originalItems = newProp.items || [];
        // Items removed from the proposal — clear ref and revert status.
        // Also walk assembly peers: if any peer of an assembly is being
        // dropped, drop ALL its peers (they can't go forward without
        // the dropped peer).
        var droppedSet = new Set();
        originalItems.forEach(function(itemId) {
          if (finalSelected.indexOf(itemId) < 0) droppedSet.add(itemId);
        });
        // Expand dropped to include peers of dropped items that are still
        // in originalItems (in case the user only deselected one peer).
        var asmIdsBeingDropped = new Set();
        droppedSet.forEach(function(id) {
          var it = allItems.find(function(x) { return x.id === id; });
          if (it && it.assemblyId) asmIdsBeingDropped.add(it.assemblyId);
        });
        originalItems.forEach(function(itemId) {
          var it = allItems.find(function(x) { return x.id === itemId; });
          if (it && it.assemblyId && asmIdsBeingDropped.has(it.assemblyId)) droppedSet.add(itemId);
        });
        droppedSet.forEach(function(itemId) {
          var it = allItems.find(function(x) { return x.id === itemId; });
          var revert = it && it.status === 'On Proposal' ? { proposalRef: '', proposalId: '', status: 'Presented to Client' } : { proposalRef: '', proposalId: '' };
          updateItem(itemId, revert);
        });
        // Newly added items — set ref and advance status to On Proposal.
        finalSelected.forEach(function(itemId) {
          if (originalItems.indexOf(itemId) < 0) {
            var it = allItems.find(function(x) { return x.id === itemId; });
            var advance = it && it.status === 'Presented to Client' ? { proposalRef: p2.proposal_ref, proposalId: p2.id, status: 'On Proposal' } : { proposalRef: p2.proposal_ref, proposalId: p2.id };
            updateItem(itemId, advance);
          }
        });
      }
      setDetailId(p.id);
      setView('detail');
      setNewProp(blankProposal());
      setSelectedIds([]);
    }

    return (
      <div style={S.wrap}>
        <div style={S.toolbar}>
          <button style={S.backBtn} onClick={function() { setView('new1'); }}>← Back</button>
          <span style={S.slash}>/</span>
          <span style={S.pageTitle}>{newProp.proposal_ref} — Select Items</span>
          {selectedIds.length > 0 && (
            <>
              <span style={{ fontSize: 11, color: '#888880', whiteSpace: 'nowrap' }}>
                {selectedIds.length} item{selectedIds.length !== 1 ? 's' : ''}
                {' · '}{fmtMoney(selTotals.total_client)}
              </span>
              <button style={S.tbtnPrimary} onClick={createProposal}>
                {proposals.some(function(x) { return x.id === newProp.id; }) ? 'Update Proposal' : 'Create Proposal'}
              </button>
            </>
          )}
        </div>

        <div style={S.content}>
          {eligible.length === 0 && (
            <div style={{ padding: '48px 0', textAlign: 'center', color: '#BBBBBB', fontSize: 13 }}>
              No items marked "Presented to Client" for {newProp.project || 'this project'}.
            </div>
          )}

          {roomKeys.map(function(rk) {
            var roomItems = roomGroups[rk];
            return (
              <div key={rk} style={{ marginBottom: 20 }}>
                <SHead>{rk}</SHead>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
                  <thead><tr>
                    <TH></TH>
                    <TH>Item</TH>
                    <TH>Status</TH>
                    <TH right>Qty</TH>
                    <TH right>Unit (Client)</TH>
                    <TH right>Total (Client)</TH>
                  </tr></thead>
                  <tbody>
                    {roomItems.map(function(it) {
                      var sel = selectedIds.indexOf(it.id) >= 0;
                      var lineTotal = assemblyClient(it, allItems);
                      var unitPrice = lineTotal / (parseQty(it.qty) || 1);
                      return (
                        <tr key={it.id}
                          onClick={function() { toggleItem(it.id); }}
                          style={{ borderBottom: '0.5px solid #EFEFEC', background: sel ? '#FFFDF0' : '#FFFFFF', cursor: 'pointer' }}
                          onMouseEnter={function(e) { if (!sel) e.currentTarget.style.background = '#F7F7F5'; }}
                          onMouseLeave={function(e) { if (!sel) e.currentTarget.style.background = '#FFFFFF'; }}>
                          <td style={{ padding: '8px 10px', width: 32 }}>
                            <div style={{
                              width: 14, height: 14, borderRadius: 3,
                              border: sel ? '1.5px solid #111111' : '1.5px solid #D8D7D2',
                              background: sel ? '#111111' : '#FFFFFF',
                              display: 'flex', alignItems: 'center', justifyContent: 'center',
                            }}>
                              {sel && <svg width="8" height="6" viewBox="0 0 9 7" fill="none">
                                <path d="M1 3.5L3.5 6L8 1" stroke="#FFF" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                              </svg>}
                            </div>
                          </td>
                          <td style={{ padding: '8px 10px', color: '#111111', fontWeight: 500 }}>
                            {it.item || it.productName || 'Untitled'}
                            {it.productName && it.item && it.productName !== it.item &&
                              <span style={{ color: '#888880', fontWeight: 400, fontSize: 11 }}> · {it.productName}</span>}
                          </td>
                          <td style={{ padding: '8px 10px', color: '#888880', fontSize: 11 }}>{it.status}</td>
                          <td style={{ padding: '8px 10px', textAlign: 'right', color: '#555552' }}>{parseQty(it.qty)}{it.qtyType ? ' ' + it.qtyType : ''}</td>
                          <td style={{ padding: '8px 10px', textAlign: 'right', color: '#555552' }}>{fmtMoney(unitPrice)}</td>
                          <td style={{ padding: '8px 10px', textAlign: 'right', fontWeight: 500, color: '#111111' }}>{fmtMoney(lineTotal)}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            );
          })}

          {selectedIds.length === 0 && eligible.length > 0 && (
            <div style={{ padding: '16px 0', textAlign: 'center', color: '#BBBBBB', fontSize: 12 }}>
              Select at least one item to continue.
            </div>
          )}
        </div>
      </div>
    );
  }

  // ── DETAIL VIEW ───────────────────────────────────────────────────────────

  if (view === 'detail' && detailId) {
    var p = proposals.find(function(x) { return x.id === detailId; });
    if (!p) { setView('list'); return null; }

    var pItems = proposalItems(p);
    var proj = resolveProject(p.project);
    var client = clientForProposal(p);

    var detailRooms = {};
    pItems.forEach(function(it) {
      var key = it.roomNumber ? (it.roomNumber + (it.roomName ? ' — ' + it.roomName : '')) : 'No Room';
      if (!detailRooms[key]) detailRooms[key] = [];
      detailRooms[key].push(it);
    });
    var detailRoomKeys = Object.keys(detailRooms).sort();

    // ── PROPOSAL PHASE SUMMARY ─────────────────────────────────────────────
    // Roll up where each item is in the procurement pipeline so the proposal
    // page shows progress at a glance — without forcing the user to flip to
    // the Tracker. Cancelled items are surfaced separately as a warning so
    // the proposal doesn't silently contain dead line items.
    var pStatusCounts = {};
    pItems.forEach(function(it) {
      var key = it.status || 'Specifying';
      pStatusCounts[key] = (pStatusCounts[key] || 0) + 1;
    });
    var pCancelledCount = pStatusCounts['Cancelled'] || 0;
    var pTotal = pItems.length;
    // Items still behind "On Proposal" — proposal moved forward in workflow
    // (Sent / Approved) but these items are still at an earlier stage.
    // Detection logic in utils.js (itemsBehindOnProposal) so it's testable.
    var pOutOfSync = itemsBehindOnProposal(p.items || [], allItems);
    var pShowSyncBanner = pOutOfSync.length > 0 && p.status !== 'Draft';
    var pReceived = (pStatusCounts['At Receiver'] || 0) + (pStatusCounts['Delivered'] || 0) + (pStatusCounts['Installed'] || 0) + (pStatusCounts['Complete'] || 0);
    var pInProduction = (pStatusCounts['PO Issued'] || 0) + (pStatusCounts['Ordered'] || 0) + (pStatusCounts['Shipped'] || 0);
    var pAwaitingDeposit = (pStatusCounts['Client Approved'] || 0);
    var pDepositPaid = (pStatusCounts['Deposit Paid'] || 0);
    var pComplete = (pStatusCounts['Complete'] || 0);
    var pPhaseSummary = (function() {
      if (pTotal === 0) return '';
      var billable = pTotal - pCancelledCount;
      if (billable === 0) return '';
      var bits = [];
      if (pComplete > 0) bits.push(pComplete + ' complete');
      if (pReceived - pComplete > 0) bits.push((pReceived - pComplete) + ' received');
      if (pInProduction > 0) bits.push(pInProduction + ' in production');
      if (pDepositPaid > 0) bits.push(pDepositPaid + ' awaiting PO');
      if (pAwaitingDeposit > 0) bits.push(pAwaitingDeposit + ' awaiting deposit');
      if (bits.length === 0) return '';
      return bits.join(' · ');
    })();

    // Apply ccFeeMul per item so the UI summary matches what the PDF
    // generator will render. Pre-2026-06-28, this reduce skipped the
    // multiplier while aggregateProposalFees() below applied it — so
    // the proposal-page UI showed correct products + wrong-by-multiplier
    // freight. Surfaced on 26HOB-P-039 (CAPTAIN'S DRESSER item carried
    // ccFeePct=549, a data-entry error). The PDF and UI now multiply
    // uniformly; ccFeeMul itself clamps out-of-range values to no-op
    // (utils.js CC_FEE_PCT_MAX).
    // Money math over LIVE items only (2026-09-03): cancelled items stay
    // visible in the room tables (struck through) but leave every total —
    // pre-fix the banner claimed "excluded from current totals" while
    // Products/Deposit Requested still counted them (26HOB-P-014).
    var pLiveItems = pItems.filter(function(it) { return it.status !== 'Cancelled'; });
    var propTotal = Math.round(pLiveItems.reduce(function(s, it) { return s + assemblyClient(it, allItems) * ccFeeMul(it); }, 0) * 100) / 100;
    // Canonical deposit-ask formula (2026-07-08): pass `proj` so the
    // cdp=100 deposit basis uses the fresh project-derived tax (Fix C),
    // and round PER ITEM — identical to the per-item DEP amount_due
    // buildDepositInvoiceRecords mints and to the billing summary cover.
    // Pre-fix this panel read the stale stored fees.salesTax and
    // disagreed with the proposal PDF + the DEP invoices.
    var propDeposit = Math.round(pLiveItems.reduce(function(s, it) {
      return s + Math.round(itemDepositAmount(it, allItems, proj) * ccFeeMul(it) * 100) / 100;
    }, 0) * 100) / 100;
    // Single canonical aggregator in utils.js — same shape consumed by
    // buildProposalPayloadPure so the summary panel and the PDF generator
    // can't drift on which fee fields they include.
    var feeAgg          = aggregateProposalFees(pLiveItems);
    var aggFreight      = feeAgg.freight;
    var aggCrating      = feeAgg.crating;
    var aggInstallation = feeAgg.installation;
    var aggSalesTax     = feeAgg.salesTax;
    var aggTariff       = feeAgg.tariff;
    var aggCustom       = feeAgg.custom;
    var hasFees         = feeAgg.hasFees;
    // Full contract value visible to the client. propDeposit is what's
    // collected at signing; propGrandTotal is what they owe in total. For
    // 100% proposals these are equal and the redundant line is suppressed.
    var aggCustomSum    = Object.keys(aggCustom).reduce(function(s, k) { return s + aggCustom[k]; }, 0);
    var propGrandTotal  = Math.round((propTotal + aggFreight + aggCrating + aggInstallation + aggSalesTax + aggTariff + aggCustomSum) * 100) / 100;
    var showGrandTotal  = Math.abs(propGrandTotal - propDeposit) >= 0.01;
    // Only advance Draft→Ready for Review→Sent; Approved handled by approveProposal()
    var nextStatus = p.status !== 'Sent' && p.status !== 'Approved'
      ? PROP_STATUSES[PROP_STATUSES.indexOf(p.status) + 1]
      : null;

    return (
      <div style={S.wrap}>

        {/* Toolbar */}
        <div style={S.toolbar}>
          <button style={S.backBtn}
            onClick={function() { setView('list'); setDeleteConfirm(false); setRevertConfirm(false); setPdfErr(''); setApproveErr(''); setMarkSentModal(null); setClientApprovedModal(null); setEditOnSentModal(null); setRevertToDraftModal(null); }}>
            ← Proposals
          </button>
          <span style={S.slash}>/</span>
          <span style={Object.assign({}, S.pageTitle, { fontFamily: 'monospace' })}>
            {p.proposal_ref || 'Draft'}
          </span>
          <StatusPill status={p.status} />
          <div style={{ width: '0.5px', height: 18, background: '#D8D7D2', margin: '0 2px', flexShrink: 0 }} />
          {p.status !== 'Approved' && (
            <button style={S.tbtnGhost} onClick={function() {
              if (p.status === 'Sent') {
                setEditOnSentModal({ proposal: p, trigger: 'edit', selectedStatus: 'Ready for Review' });
              } else {
                setNewProp(Object.assign({}, p));
                setView('edit1');
              }
            }}>
              Edit
            </button>
          )}
          {p.status !== 'Approved' && (
            <button
              style={S.tbtnGhost}
              title={p.status === 'Sent' ? 'Editing a Sent proposal will surface a status-change modal first.' : undefined}
              onClick={function() {
                if (p.status === 'Sent') {
                  setEditOnSentModal({ proposal: p, trigger: 'edit-items', selectedStatus: 'Ready for Review' });
                } else {
                  setNewProp(p);
                  setSelectedIds(p.items ? p.items.slice() : []);
                  setView('new2');
                }
              }}>
              Edit Items
            </button>
          )}
          {/* Mark Ready for Review — open to all staff, no modal. Mark Sent —
              owner+admin only, surfaces the Mark Sent modal. The
              role-gate-plus-modal pair is enforced both at this UI level
              (button suppressed when !canFinalize) and inside advanceStatus
              (early-return if !canFinalize) — defense in depth. */}
          {nextStatus === 'Ready for Review' && (
            <button style={S.tbtnGhost} onClick={function() { advanceStatus(p); }}>
              Mark Ready for Review
            </button>
          )}
          {/* "Send to Client" (renamed from "Mark Sent", 2026-07-11 —
              Cimon: the label should say what the action IS). Fuchsia =
              the in-flight zone color: this is the step that puts the
              proposal in front of the client; green (Client Approved)
              is the step that locks it. */}
          {nextStatus === 'Sent' && canFinalize && (
            <button
              style={Object.assign({}, S.tbtnPrimary,
                { background: '#D4007A', border: '0.5px solid #D4007A' })}
              onClick={function() { setMarkSentModal({ proposal: p, selectedPath: 'billing_summary' }); }}>
              Send to Client
            </button>
          )}
          {/* Revert to Draft — owner+admin only, visible only on Ready for
              Review. Routes through ConfirmationModal so the reviewer
              consciously walks the proposal back to a Draft-editable state
              before making edits. Items stay at 'On Proposal' (no cascade)
              because that's their correct steady state on a Draft per the
              createProposal forward cascade. */}
          {p.status === 'Ready for Review' && canFinalize && (
            <button
              style={S.tbtnGhost}
              data-testid="revert-to-draft-btn"
              title="Walk this proposal back to Draft so you can edit it before sending."
              onClick={function() { setRevertToDraftModal(p); }}>
              Revert to Draft
            </button>
          )}
          {/* Client Approved — owner+admin only. Renamed from "Approve +
              Assign SKU" so the user-facing label matches the action's
              meaning. Triggers the chained handler (approveProposalFromModal) via
              the modal: Gilda-side approve + QB Estimate + deposit Invoice
              all in one click. */}
          {p.status === 'Sent' && canFinalize && (
            <button
              style={Object.assign({}, S.tbtnPrimary,
                { background: '#1A5C38', border: '0.5px solid #1A5C38' })}
              onClick={function() { setClientApprovedModal(p); }}>
              Client Approved
            </button>
          )}
          <button
            style={Object.assign({}, S.tbtnGhost, pdfLoading ? { opacity: 0.5, cursor: 'default' } : {})}
            disabled={pdfLoading}
            onClick={function() { previewPdf(p); }}>
            {pdfLoading ? 'Generating…' : 'Preview PDF'}
          </button>
          <button
            style={Object.assign({}, S.tbtnGhost, pdfLoading ? { opacity: 0.5, cursor: 'default' } : {})}
            disabled={pdfLoading}
            title="One-page INVOICE — PAID print for customs paperwork. Not a Gilda invoice — no records are created."
            onClick={function() { downloadCustomsInvoice(p); }}>
            ↓ Customs Invoice
          </button>
          {/* "↓ Deposit Invoice" button removed 2026-05-25. The deposit
              invoice auto-creates at Client Approved with the full
              line-item snapshot (see approveProposal below) and is
              accessed from the Invoices tab or the Ledger view from
              that point forward. The toolbar button regenerated the
              record's content from current proposal state on every
              click, which silently overwrote the canonical snapshot
              when CO drift had shifted line items. Per the deposit-
              invoice immutability invariant, regenerating a PDF must
              read the stored snapshot, never rewrite it. A future
              "Revise Deposit Invoice" spec will define the explicit
              path for post-CO revisions (new revision row, original
              preserved). */}
          {p.status === 'Approved' && isOwner && !revertConfirm && (
            <button
              style={S.tbtnGhost}
              title="Walk this proposal back to Sent so items can be edited. Blocked if any item has advanced past Client Approved."
              onClick={function() { setRevertConfirm(true); setApproveErr(''); }}>
              Revert to Sent
            </button>
          )}
          {p.status === 'Approved' && isOwner && revertConfirm && (
            h(React.Fragment, null,
              h('span', { style: { fontSize: 11, color: '#888880', whiteSpace: 'nowrap' } },
                'Revert? Items walk back to Presented to Client.'),
              h('button', {
                style: Object.assign({}, S.tbtnDanger, approving ? { opacity: 0.5, cursor: 'default' } : {}),
                disabled: approving,
                onClick: function() { revertProposal(p); },
              }, approving ? 'Reverting…' : 'Confirm revert'),
              h('button', {
                style: S.tbtnGhost,
                onClick: function() { setRevertConfirm(false); },
              }, 'Cancel')
            )
          )}
          {!deleteConfirm
            ? <button style={S.tbtnDanger} onClick={function() { setDeleteConfirm(true); }}>Delete</button>
            : <>
                <span style={{ fontSize: 11, color: '#C8322A', whiteSpace: 'nowrap' }}>Delete?</span>
                <button style={S.tbtnDanger} onClick={function() { deleteProposal(p.id); }}>Confirm</button>
                <button style={S.tbtnGhost} onClick={function() { setDeleteConfirm(false); }}>Cancel</button>
              </>
          }
          {pdfErr && <span style={{ fontSize: 11, color: '#C8322A', whiteSpace: 'nowrap' }}>{pdfErr}</span>}
          {approveErr && <span style={{ fontSize: 11, color: '#C8322A' }}>{approveErr}</span>}
        </div>

        {/* Context strip */}
        <div style={S.ctxStrip}>
          {client && (
            <div style={S.ctxPill}>
              <span style={S.ctxLabel}>Client</span>
              <span style={{ fontSize: 11, color: '#111111', fontWeight: 500 }}>{client.name}</span>
            </div>
          )}
          {p.project && (
            <div style={S.ctxPill}>
              <span style={S.ctxLabel}>Project</span>
              <span style={{ fontSize: 11, color: '#111111' }}>{p.project}</span>
            </div>
          )}
          <div style={S.ctxPill}>
            <span style={S.ctxLabel}>Created</span>
            <span style={{ fontSize: 11, color: '#555552' }}>{fmtDate(p.created_at)}</span>
          </div>
          {p.sent_at && (
            <div style={S.ctxPill}>
              <span style={S.ctxLabel}>Sent</span>
              <span style={{ fontSize: 11, color: '#1A5C38' }}>{fmtDate(p.sent_at)}</span>
            </div>
          )}
          {p.approved_at && (
            <div style={S.ctxPill}>
              <span style={S.ctxLabel}>Approved</span>
              <span style={{ fontSize: 11, color: '#3730A3' }}>{fmtDate(p.approved_at)}</span>
            </div>
          )}
          <div style={Object.assign({}, S.ctxPill, { borderRight: 'none', marginRight: 0 })}>
            <span style={S.ctxLabel}>Items</span>
            <span style={{ fontSize: 11, color: '#555552' }}>{(p.items || []).length}</span>
          </div>
        </div>

        {/* Lock banner */}
        {p.status === 'Sent' && (
          <div style={{ padding: '9px 14px', background: '#FFF8E1', border: '0.5px solid #F5C800', fontSize: 11, color: '#BF6800', display: 'flex', alignItems: 'center', gap: 8 }}>
            <span style={{ fontSize: 13 }}>&#128274;</span>
            <span><strong>Proposal sent.</strong> Click "Approve + Assign SKU" once the client confirms to lock items and assign item numbers.</span>
          </div>
        )}
        {p.status === 'Approved' && (function() {
          // Derive the banner from actual item state, not from p.status.
          // The previous banner trusted p.status='Approved' even when item
          // updates had silently failed (race condition in approveProposal),
          // claiming all items were Client Approved when they weren't.
          var targets = collectApprovalTargets(p.items || [], allItems);
          var stuck   = targets.toApprove.filter(function(it) { return STATUSES.indexOf(it.status) < STATUSES.indexOf('Client Approved'); });
          var missingSku = targets.toApprove.filter(function(it) { return !it.itemNumber; });
          if (stuck.length === 0 && missingSku.length === 0) {
            return (
              <div style={{ padding: '9px 14px', background: '#EEF2FF', border: '0.5px solid #A5B4FC', fontSize: 11, color: '#3730A3', display: 'flex', alignItems: 'center', gap: 8 }}>
                <span style={{ fontSize: 13 }}>&#10003;</span>
                <span><strong>Client approved.</strong> All items are Client Approved and SKUs have been assigned. Ready for deposit invoicing.</span>
              </div>
            );
          }
          var bits = [];
          if (stuck.length > 0)      bits.push(stuck.length + ' item' + (stuck.length>1?'s':'') + ' not yet at Client Approved');
          if (missingSku.length > 0) bits.push(missingSku.length + ' item' + (missingSku.length>1?'s':'') + ' missing SKU');
          return (
            <div style={{ padding: '9px 14px', background: '#FFF4F4', border: '0.5px solid #C8322A', fontSize: 11, color: '#C8322A', display: 'flex', alignItems: 'center', gap: 10 }}>
              <span style={{ fontSize: 13 }}>&#9888;</span>
              <span style={{ flex: 1 }}>
                <strong>Proposal is Approved but items are out of sync:</strong> {bits.join(', ')}. Click below to retry.
              </span>
              <button onClick={function() { approveProposal(p); }} disabled={approving}
                style={{ height: 26, padding: '0 12px', borderRadius: 13, border: '1px solid #C8322A', background: '#FFFFFF', color: '#C8322A', fontSize: 11, fontWeight: 500, fontFamily: 'inherit', cursor: approving ? 'wait' : 'pointer', whiteSpace: 'nowrap' }}>
                {approving ? '…' : 'Sync now'}
              </button>
            </div>
          );
        })()}

        {/* Items out of sync — proposal advanced past Draft but items are still behind 'On Proposal' */}
        {pShowSyncBanner && (
          <div style={{ padding: '9px 14px', background: '#FFF8E1', border: '0.5px solid #F5C800', fontSize: 11, color: '#8A5400', display: 'flex', alignItems: 'center', gap: 10 }}>
            <span style={{ fontSize: 13 }}>&#9888;</span>
            <span style={{ flex: 1 }}>
              <strong>{pOutOfSync.length} item{pOutOfSync.length > 1 ? 's' : ''} out of sync.</strong>
              {' '}This proposal is {p.status} but {pOutOfSync.length === 1 ? 'this item is' : 'these items are'} still at <em>{pOutOfSync[0].status}</em> in the Tracker. Sync to bring {pOutOfSync.length === 1 ? 'it' : 'them'} to "On Proposal."
            </span>
            <button onClick={function() { syncItemsToOnProposal(p); }}
              style={{ height: 26, padding: '0 12px', borderRadius: 13, border: '1px solid #8A5400', background: '#FFFFFF', color: '#8A5400', fontSize: 11, fontWeight: 500, fontFamily: 'inherit', cursor: 'pointer', whiteSpace: 'nowrap' }}>
              Sync items
            </button>
          </div>
        )}

        {/* Cancelled-items warning */}
        {pCancelledCount > 0 && (
          <div style={{ padding: '9px 14px', background: '#FDECEA', border: '0.5px solid #F2A39C', fontSize: 11, color: '#C8322A', display: 'flex', alignItems: 'center', gap: 8 }}>
            <span style={{ fontSize: 13 }}>&#9888;</span>
            <span>
              <strong>{pCancelledCount} cancelled item{pCancelledCount > 1 ? 's' : ''}</strong> on this proposal — excluded from the totals and PDF below.
              {' '}A deposit already paid on a cancelled item becomes client credit (Credits tab); cancelled before any payment, nothing is owed back.
            </span>
          </div>
        )}

        {/* Phase summary — rolled-up procurement progress */}
        {pPhaseSummary && (
          <div style={{ padding: '9px 14px', background: '#FAFAF8', borderBottom: '0.5px solid #E0E0DC', fontSize: 11, color: '#555552', display: 'flex', alignItems: 'center', gap: 8 }}>
            <span style={{ fontSize: 10, fontWeight: 600, letterSpacing: '0.07em', textTransform: 'uppercase', color: '#888' }}>Procurement</span>
            <span>{pPhaseSummary}</span>
          </div>
        )}

        {/* Items */}
        <div style={S.content}>
          {pItems.length === 0 && (
            <div style={{ padding: '32px 0', textAlign: 'center', color: '#BBBBBB', fontSize: 13 }}>
              No items on this proposal.
              {p.status === 'Draft' && (
                <div style={{ marginTop: 10 }}>
                  <button style={{ height: 30, padding: '0 14px', borderRadius: 5, border: '0.5px solid #D8D7D2', background: '#FFFFFF', cursor: 'pointer', fontSize: 12, color: '#111111', fontFamily: 'inherit' }}
                    onClick={function() {
                      setNewProp(Object.assign({}, p));
                      setSelectedIds(p.items ? p.items.slice() : []);
                      setView('new2');
                    }}>
                    Add Items →
                  </button>
                </div>
              )}
            </div>
          )}

          {detailRoomKeys.map(function(rk) {
            var roomItems = detailRooms[rk];
            var roomTotal = roomItems.reduce(function(s, it) { return s + (it.status === 'Cancelled' ? 0 : assemblyClient(it, allItems)); }, 0);
            return (
              <div key={rk} style={{ marginBottom: 20 }}>
                <SHead>{rk}</SHead>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
                  <thead><tr>
                    <TH>Item</TH>
                    <TH>Product</TH>
                    <TH>Status</TH>
                    <TH right>Qty</TH>
                    <TH right>Unit</TH>
                    <TH right>Total</TH>
                  </tr></thead>
                  <tbody>
                    {roomItems.flatMap(function(it) {
                      var comps = allItems.filter(function(c) { return c.parentId === it.id; });
                      var hasComps = comps.length > 0;
                      var assemblyTotal = hasComps ? assemblyClient(it, allItems) : itemClient(it);
                      var unitPrice = assemblyTotal / (parseQty(it.qty) || 1);
                      var cancelled = isCancelled(it);
                      // Look up the assembly metadata for component-style items
                      // (linked via assemblyId, not the lighting/bulb parentId
                      // pattern). Used to surface the assembly name on the row
                      // so the proposal reads "Sofa — Workroom" instead of
                      // just "Workroom" with no context.
                      var asm = it.assemblyId ? (allAssemblies.find(function(a) { return a.id === it.assemblyId; }) || null) : null;
                      var asmLabel = asm && asm.name ? asm.name : '';
                      var parentRow = (
                        <tr key={it.id} style={{ borderBottom: hasComps ? 'none' : '0.5px solid #EFEFEC', background: '#FFFFFF', opacity: cancelled ? 0.55 : 1 }}>
                          <td style={{ padding: '8px 10px', color: '#111111', fontWeight: 500, textDecoration: cancelled ? 'line-through' : 'none' }}>
                            {it.item || 'Untitled'}
                            {asmLabel ? <div style={{ fontSize: 10, color: '#888', fontWeight: 400, marginTop: 2 }}>Assembly: {asmLabel}</div> : null}
                          </td>
                          <td style={{ padding: '8px 10px', color: '#555552' }}>{it.productName || (hasComps ? '' : '—')}</td>
                          <td style={{ padding: '8px 10px', color: '#555552', whiteSpace: 'nowrap' }}>
                            <span style={{ display: 'inline-block', width: 8, height: 8, borderRadius: '50%', background: statusColor(it.status), marginRight: 6, verticalAlign: 'middle' }} />
                            {it.status || '—'}
                          </td>
                          <td style={{ padding: '8px 10px', textAlign: 'right', color: '#555552' }}>{parseQty(it.qty)}{it.qtyType ? ' ' + it.qtyType : ''}</td>
                          <td style={{ padding: '8px 10px', textAlign: 'right', color: hasComps ? '#888880' : '#555552' }}>{hasComps ? '' : fmtMoney(unitPrice)}</td>
                          <td style={{ padding: '8px 10px', textAlign: 'right', fontWeight: 500, color: '#111111' }}>{fmtMoney(assemblyTotal)}</td>
                        </tr>
                      );
                      if (!hasComps) return [parentRow];
                      var compRows = comps.map(function(comp, ci) {
                        var compTotal = itemClient(comp);
                        var compUnit = compTotal / (parseQty(comp.qty) || 1);
                        return (
                          <tr key={comp.id} style={{ borderBottom: ci === comps.length - 1 ? '0.5px solid #EFEFEC' : '0.5px solid #F3F3F0', background: '#FAFAFA' }}>
                            <td style={{ padding: '5px 10px 5px 22px', color: '#555552', fontSize: 11 }}>
                              <span style={{ color: '#C8C8C4', marginRight: 5 }}>↳</span>{comp.item || 'Untitled'}
                            </td>
                            <td style={{ padding: '5px 10px', color: '#888880', fontSize: 11 }}>{comp.productName || '—'}</td>
                            <td style={{ padding: '5px 10px', color: '#aaa', fontSize: 11 }}>—</td>
                            <td style={{ padding: '5px 10px', textAlign: 'right', color: '#888880', fontSize: 11 }}>{parseQty(comp.qty)}{comp.qtyType ? ' ' + comp.qtyType : ''}</td>
                            <td style={{ padding: '5px 10px', textAlign: 'right', color: '#888880', fontSize: 11 }}>{fmtMoney(compUnit)}</td>
                            <td style={{ padding: '5px 10px', textAlign: 'right', color: '#888880', fontSize: 11 }}>{fmtMoney(compTotal)}</td>
                          </tr>
                        );
                      });
                      return [parentRow].concat(compRows);
                    })}
                  </tbody>
                  <tfoot>
                    <tr style={{ borderTop: '0.5px solid #D8D7D2' }}>
                      <td colSpan={5} style={{ padding: '6px 10px', textAlign: 'right', fontSize: 10, fontWeight: 500, letterSpacing: '0.07em', textTransform: 'uppercase', color: '#888880' }}>
                        Room subtotal
                      </td>
                      <td style={{ padding: '6px 10px', textAlign: 'right', fontWeight: 500, color: '#111111' }}>
                        {fmtMoney(Math.round(roomTotal * 100) / 100)}
                      </td>
                    </tr>
                  </tfoot>
                </table>
              </div>
            );
          })}

          {/* Grand total */}
          {pItems.length > 0 && (
            <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 8, marginBottom: 20 }}>
              <div style={{ minWidth: 280, borderTop: '0.5px solid #D8D7D2', paddingTop: 12 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 6, fontSize: 12, color: '#555552' }}>
                  <span>Products</span>
                  <span style={{ fontWeight: 500, color: '#111111' }}>{fmtMoney(propTotal)}</span>
                </div>
                {aggFreight > 0 && (
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4, fontSize: 12, color: '#555552' }}>
                    <span>Freight</span>
                    <span>{fmtMoney(aggFreight)}</span>
                  </div>
                )}
                {aggCrating > 0 && (
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4, fontSize: 12, color: '#555552' }}>
                    <span>Crating</span>
                    <span>{fmtMoney(aggCrating)}</span>
                  </div>
                )}
                {aggInstallation > 0 && (
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4, fontSize: 12, color: '#555552' }}>
                    <span>Installation</span>
                    <span>{fmtMoney(aggInstallation)}</span>
                  </div>
                )}
                {aggSalesTax > 0 && (
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4, fontSize: 12, color: '#555552' }}>
                    <span>Sales Tax</span>
                    <span>{fmtMoney(aggSalesTax)}</span>
                  </div>
                )}
                {aggTariff > 0 && (
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4, fontSize: 12, color: '#555552' }}>
                    <span>Tariffs</span>
                    <span>{fmtMoney(aggTariff)}</span>
                  </div>
                )}
                {Object.keys(aggCustom).map(function(lbl) {
                  return (
                    <div key={lbl} style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4, fontSize: 12, color: '#555552' }}>
                      <span>{lbl}</span>
                      <span>{fmtMoney(Math.round(aggCustom[lbl] * 100) / 100)}</span>
                    </div>
                  );
                })}
                {showGrandTotal && (
                  <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13, fontWeight: 500, color: '#111111', borderTop: '0.5px solid #EFEFEC', paddingTop: 6, marginTop: hasFees ? 4 : 0 }}>
                    <span>Proposal Total</span>
                    <span>{fmtMoney(propGrandTotal)}</span>
                  </div>
                )}
                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13, fontWeight: 500, color: '#111111', borderTop: showGrandTotal ? '0.5px solid #EFEFEC' : '0.5px solid #EFEFEC', paddingTop: 6, marginTop: (hasFees && !showGrandTotal) ? 4 : 0 }}>
                  <span>Deposit Requested</span>
                  <span>{fmtMoney(propDeposit)}</span>
                </div>
              </div>
            </div>
          )}

          {/* Notes */}
          {p.notes && (
            <div style={{ marginBottom: 20, padding: '10px 14px', background: '#F7F7F5', border: '0.5px solid #D8D7D2', borderRadius: 4 }}>
              <div style={{ fontSize: 10, fontWeight: 500, letterSpacing: '0.07em', textTransform: 'uppercase', color: '#888880', marginBottom: 4 }}>Notes</div>
              <div style={{ fontSize: 12, color: '#555552' }}>{p.notes}</div>
            </div>
          )}
        </div>

        {/* ── Mark Sent two-path modal ────────────────────────────────────
            Replaces the prior single-path version. Two radio options
            decide HOW the proposal is being delivered:
              · billing_summary (default) — bundled into the next monthly
                client billing summary doc.
              · off_cycle — delivered out of band; NEVER auto-included on
                a summary.
            On confirm: advanceStatus(target, selectedPath) which writes
            sent_via + sent_off_cycle_at into the patch alongside sent_at,
            and fires the existing item-status cascade for both paths. */}
        {markSentModal && (
          <ConfirmationModal
            open={true}
            heading="Mark proposal as Sent?"
            body={(function() {
              var name = displayNameFor(ctx.userEmail, ctx.firmSettings);
              var greet = name ? 'Hi ' + name + '! ' : '';
              return greet + 'You\u2019re about to mark this proposal as Sent. How is it being delivered?';
            })()}
            confirmLabel="Confirm"
            cancelLabel="Cancel"
            onCancel={function() { setMarkSentModal(null); }}
            onConfirm={function() {
              var ctx2 = markSentModal;
              setMarkSentModal(null);
              if (ctx2 && ctx2.proposal) advanceStatus(ctx2.proposal, ctx2.selectedPath);
            }}
          >
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8, fontSize: 13, color: '#111' }}>
              {[
                { value: 'billing_summary', label: 'Include on the next billing summary (default)' },
                { value: 'off_cycle',       label: 'Send off-cycle' },
              ].map(function(opt) {
                var checked = markSentModal.selectedPath === opt.value;
                return (
                  <label key={opt.value} style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer' }}>
                    <input
                      type="radio"
                      name="markSentPath"
                      value={opt.value}
                      checked={checked}
                      onChange={function() {
                        setMarkSentModal(Object.assign({}, markSentModal, { selectedPath: opt.value }));
                      }}
                      style={{ cursor: 'pointer' }}
                      data-testid={'mark-sent-option-' + opt.value.replace(/_/g, '-')}
                    />
                    <span>{opt.label}</span>
                  </label>
                );
              })}
            </div>
          </ConfirmationModal>
        )}

        {/* ── Client Approved confirmation modal ──────────────────────────
            Heavy confirmation. Click confirm → approveProposalFromModal(p), the
            chained handler that runs Gilda-side approve. Under manual sync
            mode (default per SPEC 1), no QB push fires. Under auto mode,
            the legacy cascade triggers Estimate + deposit Invoice creation.
            confirming flag uses approving || qbPushing so the modal
            disables its buttons through both phases. On error the modal's
            error slot renders setApproveErr's value; retries happen from
            the QuickBooks tab. */}
        <ConfirmationModal
          open={!!clientApprovedModal}
          heading="Mark proposal as Client Approved?"
          body={(function() {
            var name = displayNameFor(ctx.userEmail, ctx.firmSettings);
            var greet = name ? 'Hi ' + name + '! ' : '';
            return greet + 'You\u2019re about to mark this proposal as Client Approved. '
              + 'This will lock all items, assign each one a SKU, and create the deposit invoice. '
              + 'Material fields will lock \u2014 further edits require a Change Order. Are you sure?';
          })()}
          confirmLabel="Confirm"
          cancelLabel="Cancel"
          confirming={approving}
          tone="primary"
          error={approveErr || ''}
          onCancel={function() { setClientApprovedModal(null); setApproveErr(''); }}
          onConfirm={async function() {
            var target = clientApprovedModal;
            if (!target) return;
            var result = await approveProposalFromModal(target);
            if (result && result.ok) {
              setClientApprovedModal(null);
            }
            // On failure: keep modal open if approve itself failed (so
            // user can read setApproveErr in the modal's error slot and
            // cancel). If the QB-push half failed, the chained handler
            // already set setApproveErr; the bookkeeper can cancel the
            // modal and use the toolbar's standalone Push retry.
          }}
        />

        {/* ── Edit-on-Sent three-option modal ─────────────────────────────
            Triggered by Edit and Edit Items when proposal status is Sent.
            Three radio options: move to Ready for Review (default), Stay
            at Sent, or Draft. On confirm: optional status flip + stamp
            lastEditedAt + open the existing edit flow (edit1 or new2
            depending on which button triggered the modal). */}
        {editOnSentModal && (
          <ConfirmationModal
            open={true}
            heading="Edit this proposal?"
            body="You are changing a proposal that the client has been shown. If you would like to do this, choose the preferred status:"
            confirmLabel="Confirm and edit"
            cancelLabel="Cancel"
            onCancel={function() { setEditOnSentModal(null); }}
            onConfirm={async function() {
              var ctx2 = editOnSentModal;
              if (!ctx2 || !ctx2.proposal) return;
              var target = ctx2.proposal;
              var nextStatus = ctx2.selectedStatus;  // 'Ready for Review' | 'Sent' | 'Draft'
              var patch = { lastEditedAt: new Date().toISOString() };
              if (nextStatus !== 'Sent') patch.status = nextStatus;
              // Await the save so the edit flow opens against the freshly
              // saved row — prevents the next save from racing with this
              // status flip.
              await saveProposal(Object.assign({}, target, patch));
              if (ctx2.trigger === 'edit-items') {
                setNewProp(Object.assign({}, target, patch));
                setSelectedIds(target.items ? target.items.slice() : []);
                setView('new2');
              } else {
                setNewProp(Object.assign({}, target, patch));
                setView('edit1');
              }
              setEditOnSentModal(null);
            }}
          >
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8, fontSize: 13, color: '#111' }}>
              {/* Packaged-proposal warning (2026-07-27, the 26HOB-P-052
                  lesson). billed_on_summary_id is only ever written by a
                  package SEND, so a tagged proposal is on a package the
                  client received. Walking its status back doesn't untag
                  it, but it makes the proposal read as unsent and takes
                  a SQL fix to restore cleanly — make that a conscious
                  choice. */}
              {editOnSentModal.proposal && editOnSentModal.proposal.billedOnSummaryId && (
                <div style={{ border: '0.5px solid #C99700', borderRadius: 5, padding: '8px 10px', fontSize: 12, color: '#8A6D00', lineHeight: 1.5 }}>
                  This proposal is on a billing summary package the client
                  already received. Choose <b>Stay at Sent</b> unless you
                  deliberately want it to read as unsent.
                </div>
              )}
              {['Ready for Review', 'Sent', 'Draft'].map(function(opt) {
                var label = opt === 'Sent' ? 'Stay at Sent' : opt;
                var checked = editOnSentModal.selectedStatus === opt;
                return (
                  <label key={opt} style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer' }}>
                    <input
                      type="radio"
                      name="editOnSentTarget"
                      value={opt}
                      checked={checked}
                      onChange={function() {
                        setEditOnSentModal(Object.assign({}, editOnSentModal, { selectedStatus: opt }));
                      }}
                      style={{ cursor: 'pointer' }}
                      data-testid={'edit-on-sent-option-' + opt.toLowerCase().replace(/\s+/g, '-')}
                    />
                    <span>{label}</span>
                  </label>
                );
              })}
            </div>
          </ConfirmationModal>
        )}

        {/* ── Revert-to-Draft modal ─────────────────────────────────────
            Triggered by the "Revert to Draft" button on a Ready-for-Review
            proposal (owner+admin only). Pure status flip — no item cascade
            and no timestamp clearing. Items on a Draft proposal are already
            at 'On Proposal' (createProposal forward cascade fires at item-
            add time, not on Mark Ready), so the revert needs no item walk-
            back. */}
        {revertToDraftModal && (
          <ConfirmationModal
            open={true}
            heading="Revert to Draft?"
            body={(function() {
              var name = displayNameFor(ctx.userEmail, ctx.firmSettings);
              var greet = name ? 'Hi ' + name + ' \u2014 ' : '';
              return greet + 'this will return the proposal to Draft state so it can be edited.\n\n'
                + 'Items on the proposal will stay on the proposal. Continue?';
            })()}
            confirmLabel="Revert to Draft"
            cancelLabel="Cancel"
            tone="primary"
            onCancel={function() { setRevertToDraftModal(null); }}
            onConfirm={async function() {
              var target = revertToDraftModal;
              if (!target) return;
              await revertProposalToDraft(target);
              setRevertToDraftModal(null);
            }}
          />
        )}
      </div>
    );
  }

  // ── LIST VIEW ─────────────────────────────────────────────────────────────

  var filterProjects = Array.from(new Set(
    proposals.map(function(p) { return p.project || ''; }).filter(Boolean)
  )).sort();
  var visibleProposals = (projFilter
    ? proposals.filter(function(p) { return p.project === projFilter; })
    : proposals)
    .slice()
    .sort(function(a, b) { return extractProposalSeq(a.proposal_ref) - extractProposalSeq(b.proposal_ref); });
  var draftCount = visibleProposals.filter(function(p) { return p.status === 'Draft'; }).length;

  return (
    <div style={S.wrap}>

      {/* Toolbar */}
      <div style={S.toolbar}>
        <span style={S.pageTitle}>Proposals</span>
        {draftCount > 0 && (
          <span style={{ fontSize: 10, color: '#888880', letterSpacing: '0.07em', textTransform: 'uppercase', whiteSpace: 'nowrap' }}>
            {draftCount} draft{draftCount !== 1 ? 's' : ''}
          </span>
        )}
        <button style={S.tbtnPrimary}
          onClick={function() { setNewProp(blankProposal()); setView('new1'); }}>
          + New Proposal
        </button>
      </div>

      {/* Project filter ribbon */}
      {filterProjects.length > 0 && (
        <div style={{display:'flex',alignItems:'center',height:36,padding:'0 14px',gap:6,overflowX:'auto',borderBottom:'0.5px solid #D8D7D2',background:'#FFFFFF',flexShrink:0}}>
          <button
            onClick={function() { setProjFilter(''); }}
            style={{height:24,padding:'0 10px',borderRadius:12,border:projFilter===''?'1.5px solid #111111':'0.5px solid #D8D7D2',background:projFilter===''?'#111111':'#FFFFFF',color:projFilter===''?'#FFFFFF':'#888880',fontSize:11,fontWeight:500,cursor:'pointer',fontFamily:'inherit',flexShrink:0,whiteSpace:'nowrap'}}>
            All
          </button>
          {filterProjects.map(function(proj) {
            var active = projFilter === proj;
            return (
              <button key={proj}
                onClick={function() { setProjFilter(active ? '' : proj); }}
                style={{height:24,padding:'0 10px',borderRadius:12,border:active?'1.5px solid #111111':'0.5px solid #D8D7D2',background:active?'#111111':'#FFFFFF',color:active?'#FFFFFF':'#888880',fontSize:11,fontWeight:500,cursor:'pointer',fontFamily:'inherit',flexShrink:0,whiteSpace:'nowrap'}}>
                {proj}
              </button>
            );
          })}
        </div>
      )}

      {/* List */}
      <div style={S.content}>
        {proposals.length === 0 && (
          <div style={{ padding: '64px 0', textAlign: 'center', color: '#BBBBBB', fontSize: 13 }}>
            No proposals yet. Click "+ New Proposal" to create one.
          </div>
        )}

        {proposals.length > 0 && visibleProposals.length === 0 && (
          <div style={{ padding: '64px 0', textAlign: 'center', color: '#BBBBBB', fontSize: 13 }}>
            No proposals for this project.
          </div>
        )}

        {visibleProposals.length > 0 && (function() {
          var listTotalClient = visibleProposals.reduce(function(s, p) { return s + (parseFloat(p.total_client) || 0); }, 0);
          var listTotalDeposit = visibleProposals.reduce(function(s, p) { return s + (parseFloat(p.total_deposit) || 0); }, 0);
          return (
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
              <thead><tr>
                <TH>Ref</TH>
                <TH>Name</TH>
                <TH>Client</TH>
                <TH right>Items</TH>
                <TH right>Client Total</TH>
                <TH right>Deposit</TH>
                <TH>Status</TH>
                <TH>Created</TH>
                <TH></TH>
              </tr></thead>
              <tbody>
                {visibleProposals.map(function(p) {
                  var cl = clientForProposal(p);
                  return (
                    <tr key={p.id}
                      onClick={function() { setDetailId(p.id); setDeleteConfirm(false); setPdfErr(''); setView('detail'); }}
                      style={{ borderBottom: '0.5px solid #EFEFEC', cursor: 'pointer', background: '#FFFFFF' }}
                      onMouseEnter={function(e) { e.currentTarget.style.background = '#F7F7F5'; }}
                      onMouseLeave={function(e) { e.currentTarget.style.background = '#FFFFFF'; }}>
                      <td style={{ padding: '10px 10px', fontFamily: 'monospace', fontSize: 11, color: '#111111', fontWeight: 500 }}>
                        {p.proposal_ref || '—'}
                      </td>
                      <td style={{ padding: '10px 10px', color: '#111111', fontWeight: 500 }}>{p.proposal_name || '—'}</td>
                      <td style={{ padding: '10px 10px', color: '#555552' }}>{cl ? cl.name : '—'}</td>
                      <td style={{ padding: '10px 10px', textAlign: 'right', color: '#555552' }}>{(p.items || []).length}</td>
                      <td style={{ padding: '10px 10px', textAlign: 'right', fontWeight: 500, color: '#111111' }}>{fmtMoney(p.total_client)}</td>
                      <td style={{ padding: '10px 10px', textAlign: 'right', color: '#555552' }}>{fmtMoney(p.total_deposit)}</td>
                      <td style={{ padding: '10px 10px' }}><StatusPill status={p.status} /></td>
                      <td style={{ padding: '10px 10px', color: '#888880' }}>{fmtDate(p.created_at)}</td>
                      <td style={{ padding: '10px 10px', textAlign: 'right', color: '#888880', fontSize: 11 }}>Open →</td>
                    </tr>
                  );
                })}
              </tbody>
              <tfoot>
                <tr style={{ borderTop: '0.5px solid #D8D7D2' }}>
                  <td colSpan={4} style={{ padding: '7px 10px', textAlign: 'right', fontSize: 10, fontWeight: 500, letterSpacing: '0.07em', textTransform: 'uppercase', color: '#888880' }}>
                    {visibleProposals.length} proposal{visibleProposals.length !== 1 ? 's' : ''}
                  </td>
                  <td style={{ padding: '7px 10px', textAlign: 'right', fontWeight: 500, color: '#111111', fontSize: 11 }}>{fmtMoney(Math.round(listTotalClient * 100) / 100)}</td>
                  <td style={{ padding: '7px 10px', textAlign: 'right', color: '#555552', fontSize: 11 }}>{fmtMoney(Math.round(listTotalDeposit * 100) / 100)}</td>
                  <td colSpan={3}></td>
                </tr>
              </tfoot>
            </table>
          );
        })()}
      </div>
    </div>
  );
}
